import React, { forwardRef, useState, createContext, useRef, useEffect, useContext, useMemo, useCallback } from 'react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import styled18 from '@emotion/styled';
import { css, ThemeProvider } from '@emotion/react';
import { uniqueId } from 'lodash-es';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { IconLockFilled } from '@tabler/icons-react';

// src/components/Button/index.tsx

// src/theme/tokens/colors.ts
var colors = {
  "color-global-blue-99": "#F5FAFF",
  "color-global-blue-95": "#E6F2FF",
  "color-global-blue-90": "#CCE4FF",
  "color-global-blue-50": "#007AFF",
  "color-global-blue-45": "#006FE8",
  "color-global-blue-40": "#0057B5",
  "color-global-neutral-99": "#F7F7F8",
  "color-global-neutral-98": "#F4F4F5",
  "color-global-neutral-97": "#EAEBEC",
  "color-global-neutral-96": "#E1E2E4",
  "color-global-neutral-95": "#DBDCDF",
  "color-global-neutral-90": "#C2C4C8",
  "color-global-neutral-70": "#989BA2",
  "color-global-neutral-50": "#70737C",
  "color-global-neutral-30": "#46474C",
  "color-global-neutral-22": "#2E2F33",
  "color-global-neutral-15": "#1B1C1E",
  "color-global-neutral-10": "#171719",
  "color-global-neutral-5": "#0F0F10",
  "color-global-common-0": "#000000",
  "color-global-common-100": "#ffffff",
  "color-global-red-99": "#FFF7F7",
  "color-global-red-95": "#FFECEC",
  "color-global-red-90": "#FFD2D2",
  "color-global-red-50": "#FF4242",
  "color-global-red-40": "#D93838",
  "color-global-orange-99": "#FFF9F2",
  "color-global-orange-95": "#FFF4E6",
  "color-global-orange-90": "#FFE5C2",
  "color-global-orange-50": "#FF9200",
  "color-global-orange-40": "#D97C00",
  "color-global-green-99": "#F2F9F3",
  "color-global-green-95": "#E6F9EA",
  "color-global-green-90": "#C2F1CD",
  "color-global-green-50": "#00C32E",
  "color-global-green-40": "#00A627",
  "color-global-sky-99": "#EDF7F9",
  "color-global-sky-95": "#E0F5F9",
  "color-global-sky-90": "#C2EAF1",
  "color-global-sky-50": "#00A9C3",
  "color-global-sky-40": "#0090A6",
  "color-global-deepblue-99": "#F2F4FF",
  "color-global-deepblue-95": "#E5E8FF",
  "color-global-deepblue-90": "#D4DAFF",
  "color-global-deepblue-50": "#4B63FF",
  "color-global-deepblue-40": "#4054D9",
  "color-global-purple-99": "#FAF2FF",
  "color-global-purple-95": "#F5E5FF",
  "color-global-purple-90": "#EFD4FF",
  "color-global-purple-50": "#BB4DFF",
  "color-global-purple-40": "#9F41D9",
  "color-global-pink-99": "#FFF2FA",
  "color-global-pink-95": "#FFE5F4",
  "color-global-pink-90": "#FFD2EC",
  "color-global-pink-50": "#FF44B1",
  "color-global-pink-40": "#D93A96",
  "color-global-opacity": {
    5: 0.05,
    8: 0.08,
    12: 0.12,
    16: 0.16,
    22: 0.22,
    28: 0.28,
    35: 0.35,
    43: 0.43,
    52: 0.52,
    61: 0.61,
    74: 0.74,
    88: 0.88,
    97: 0.97
  }
};

// src/theme/themes/themes.ts
var themes = {
  common: {
    commonWhite: colors["color-global-common-100"],
    commonBlack: colors["color-global-common-0"]
  },
  primary: {
    primaryNormal: colors["color-global-blue-50"],
    primaryNeutral: colors["color-global-blue-90"],
    primaryAlternative: colors["color-global-blue-95"],
    primaryAssistive: colors["color-global-blue-99"],
    primaryStrong: colors["color-global-blue-45"],
    primaryHeavy: colors["color-global-blue-40"]
  },
  label: {
    labelNormal: colors["color-global-neutral-10"],
    labelStrong: colors["color-global-neutral-5"],
    labelNeutral: colors["color-global-neutral-30"],
    labelAlternative: colors["color-global-neutral-50"],
    labelAssistive: colors["color-global-neutral-70"],
    labelDisable: colors["color-global-neutral-90"]
  },
  background: {
    backgroundNormal: colors["color-global-common-100"],
    backgroundAlternative: colors["color-global-neutral-99"]
  },
  line: {
    lineStrong: colors["color-global-neutral-90"],
    lineNormal: colors["color-global-neutral-95"],
    lineNeutral: colors["color-global-neutral-97"],
    lineAlternative: colors["color-global-neutral-99"]
  },
  fill: {
    fillNormal: colors["color-global-neutral-96"],
    fillNeutral: colors["color-global-neutral-97"],
    fillAlternative: colors["color-global-neutral-98"],
    fillAssistive: colors["color-global-neutral-99"],
    fillStrong: colors["color-global-neutral-22"],
    fillHeavy: colors["color-global-neutral-15"]
  },
  accent: {
    accentSky: colors["color-global-sky-50"],
    accentSkyStrong: colors["color-global-sky-40"],
    accentSkyNeutral: colors["color-global-sky-90"],
    accentSkyAlternative: colors["color-global-sky-95"],
    accentSkyAssistive: colors["color-global-sky-99"],
    accentDeepblue: colors["color-global-deepblue-50"],
    accentDeepblueStrong: colors["color-global-deepblue-40"],
    accentDeepblueNeutral: colors["color-global-deepblue-90"],
    accentDeepblueAlternative: colors["color-global-deepblue-95"],
    accentDeepblueAssistive: colors["color-global-deepblue-99"],
    accentPurple: colors["color-global-purple-50"],
    accentPurpleStrong: colors["color-global-purple-40"],
    accentPurpleNeutral: colors["color-global-purple-90"],
    accentPurpleAlternative: colors["color-global-purple-95"],
    accentPurpleAssistive: colors["color-global-purple-99"],
    accentPink: colors["color-global-pink-50"],
    accentPinkStrong: colors["color-global-pink-40"],
    accentPinkNeutral: colors["color-global-pink-90"],
    accentPinkAlternative: colors["color-global-pink-95"],
    accentPinkAssistive: colors["color-global-pink-99"],
    accentGreen: colors["color-global-green-50"],
    accentGreenStrong: colors["color-global-green-40"],
    accentGreenNeutral: colors["color-global-green-90"],
    accentGreenAlternative: colors["color-global-green-95"],
    accentGreenAssistive: colors["color-global-green-99"],
    accentOrange: colors["color-global-orange-50"],
    accentOrangeStrong: colors["color-global-orange-40"],
    accentOrangeNeutral: colors["color-global-orange-90"],
    accentOrangeAlternative: colors["color-global-orange-95"],
    accentOrangeAssistive: colors["color-global-orange-99"],
    accentRed: colors["color-global-red-50"],
    accentRedStrong: colors["color-global-red-40"],
    accentRedNeutral: colors["color-global-red-90"],
    accentRedAlternative: colors["color-global-red-95"],
    accentRedAssistive: colors["color-global-red-99"]
  },
  status: {
    statusPositive: colors["color-global-green-50"],
    statusCautionary: colors["color-global-orange-50"],
    statusNegative: colors["color-global-red-50"]
  },
  opacity: {
    opacity5: colors["color-global-opacity"][5],
    opacity8: colors["color-global-opacity"][8],
    opacity12: colors["color-global-opacity"][12],
    opacity16: colors["color-global-opacity"][16],
    opacity22: colors["color-global-opacity"][22],
    opacity28: colors["color-global-opacity"][28],
    opacity35: colors["color-global-opacity"][35],
    opacity43: colors["color-global-opacity"][43],
    opacity52: colors["color-global-opacity"][52],
    opacity61: colors["color-global-opacity"][61],
    opacity74: colors["color-global-opacity"][74],
    opacity88: colors["color-global-opacity"][88],
    opacity97: colors["color-global-opacity"][97]
  },
  shadow: {
    shadowXS: "0px 1px 2px rgba(0, 0, 0, 0.08)",
    shadowS: " 0px 1px 2px 0px rgba(0, 0, 0, 0.08), 0px 2px 4px 0px rgba(0, 0, 0, 0.08)",
    shadowM: "0px 2px 8px 0px rgba(0, 0, 0, 0.08), 0px 6px 12px 0px rgba(0, 0, 0, 0.08)",
    shadowL: "0px 2px 8px 0px rgba(0, 0, 0, 0.08), 0px 12px 16px 0px rgba(0, 0, 0, 0.08)"
  },
  typography: {
    fontFamily: "Pretendard",
    title1: {
      fontSize: "36px",
      lineHeight: "150%",
      fontWeight: "500"
    },
    title2: {
      fontSize: "28px",
      lineHeight: "150%",
      fontWeight: "500"
    },
    subTitle1: {
      fontSize: "24px",
      lineHeight: "150%",
      fontWeight: "500"
    },
    subTitle2: {
      fontSize: "20px",
      lineHeight: "150%",
      fontWeight: "500"
    },
    subTitle3: {
      fontSize: "18px",
      lineHeight: "150%",
      fontWeight: "500"
    },
    body1: {
      fontSize: "18px",
      lineHeight: "150%",
      letterSpacing: "-0.02em",
      fontWeight: "500"
    },
    body2: {
      fontSize: "16px",
      lineHeight: "150%",
      letterSpacing: "-0.02em",
      fontWeight: "500"
    },
    body3: {
      fontSize: "14px",
      lineHeight: "150%",
      letterSpacing: "-0.02em",
      fontWeight: "500"
    },
    body4: {
      fontSize: "12px",
      lineHeight: "150%",
      letterSpacing: "-0.02em",
      fontWeight: "500"
    },
    caption1: {
      fontSize: "11px",
      lineHeight: "150%",
      letterSpacing: "-0.02em",
      fontWeight: "500"
    }
  }
};

// src/styles/utils/color.ts
var colorNameToHex = (color) => {
  if (color in themes.accent) return themes.accent[color];
  if (color in themes.background) return themes.background[color];
  if (color in themes.common) return themes.common[color];
  if (color in themes.primary) return themes.primary[color];
  if (color in themes.label) return themes.label[color];
  if (color in themes.line) return themes.line[color];
  if (color in themes.status) return themes.status[color];
  return themes.fill[color];
};
[
  ...Object.keys(themes.accent),
  ...Object.keys(themes.background),
  ...Object.keys(themes.common),
  ...Object.keys(themes.primary),
  ...Object.keys(themes.label),
  ...Object.keys(themes.line),
  ...Object.keys(themes.status),
  ...Object.keys(themes.fill)
];
var SearchIcon = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M15.8163 15L21.8163 21M10.8163 17C6.95034 17 3.81633 13.866 3.81633 10C3.81633 6.13401 6.95034 3 10.8163 3C14.6823 3 17.8163 6.13401 17.8163 10C17.8163 13.866 14.6823 17 10.8163 17Z",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Search_default = SearchIcon;
var CheckSuccessIcon = ({ iconSize: iconSize2 = 28, iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 28 28",
      fill: "none",
      children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M24.5 14C24.5 19.799 19.799 24.5 14 24.5C8.20101 24.5 3.5 19.799 3.5 14C3.5 8.20101 8.20101 3.5 14 3.5C19.799 3.5 24.5 8.20101 24.5 14Z",
            fill: colorNameToHex("statusPositive")
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M9.33398 14L12.834 17.5L18.6673 10.5",
            stroke: "white",
            strokeWidth: iconStroke,
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        )
      ]
    }
  );
};
var CheckSuccess_default = CheckSuccessIcon;
var CheckWarningIcon = ({ iconSize: iconSize2 = 28, iconStroke = 2 }) => {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 28 28",
      fill: "none",
      children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M3.5 14C3.5 19.799 8.20101 24.5 14 24.5C19.799 24.5 24.5 19.799 24.5 14C24.5 8.20101 19.799 3.5 14 3.5C8.20101 3.5 3.5 8.20101 3.5 14Z",
            fill: colorNameToHex("statusCautionary")
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M13.9995 9.85852V14.5252M14.0576 18.0252V18.1419L13.9414 18.1414V18.0252H14.0576Z",
            stroke: "white",
            strokeWidth: iconStroke,
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        )
      ]
    }
  );
};
var CheckWarning_default = CheckWarningIcon;
var CheckDangerIcon = ({ iconSize: iconSize2 = 28, iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 28 28",
      fill: "none",
      children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M24.5 14C24.5 19.799 19.799 24.5 14 24.5C8.20101 24.5 3.5 19.799 3.5 14C3.5 8.20101 8.20101 3.5 14 3.5C19.799 3.5 24.5 8.20101 24.5 14Z",
            fill: colorNameToHex("statusNegative")
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M17.5 17.5L14 14M14 14L10.5 10.5M14 14L17.5 10.5M14 14L10.5 17.5",
            stroke: "white",
            strokeWidth: iconStroke,
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        )
      ]
    }
  );
};
var CheckDanger_default = CheckDangerIcon;
var ArrowChevronLg = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5,
  direction = "right"
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      style: {
        transform: direction === "right" ? "rotate(0deg)" : direction === "left" ? "rotate(180deg)" : direction === "up" ? "rotate(90deg)" : "rotate(-90deg)"
      },
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M10 8L14 12L10 16",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var ArrowChevronLg_default = ArrowChevronLg;
var ArrowChevronM = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5,
  direction = "right"
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      style: {
        transition: "transform 0.2s ease",
        transform: direction === "right" ? "rotate(0deg)" : direction === "left" ? "rotate(180deg)" : direction === "down" ? "rotate(90deg)" : "rotate(-90deg)"
      },
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M10 8L14 12L10 16",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var ArrowChevronM_default = ArrowChevronM;
var ArrowLg = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5,
  direction = "right"
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      style: {
        transform: direction === "right" ? "rotate(0deg)" : direction === "left" ? "rotate(180deg)" : direction === "up" ? "rotate(90deg)" : "rotate(-90deg)"
      },
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M21 12L16 7M21 12L16 17M21 12H3",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var ArrowLg_default = ArrowLg;
var ArrowMd = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5,
  direction = "right"
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      style: {
        transform: direction === "right" ? "rotate(0deg)" : direction === "left" ? "rotate(180deg)" : direction === "up" ? "rotate(90deg)" : "rotate(-90deg)"
      },
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M5 12H19M19 12L13 6M19 12L13 18",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var ArrowMd_default = ArrowMd;
var ArrowSm = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5,
  direction = "right"
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      style: {
        transform: direction === "right" ? "rotate(0deg)" : direction === "left" ? "rotate(180deg)" : direction === "up" ? "rotate(90deg)" : "rotate(-90deg)"
      },
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M7 12H17M17 12L13 8M17 12L13 16",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var ArrowSm_default = ArrowSm;
var Flag = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M4 21V15.6871M4 15.6871C9.81818 11.1377 14.1818 20.2363 20 15.6869V4.31347C14.1818 8.86284 9.81818 -0.236103 4 4.31327V15.6871Z",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Flag_default = Flag;
var Chartup = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M3.63265 15.0002V16.8C3.63265 17.9201 3.63265 18.4798 3.85064 18.9076C4.04239 19.2839 4.34812 19.5905 4.72445 19.7822C5.15185 20 5.71165 20 6.82956 20H21.6328M3.63265 15.0002V5M3.63265 15.0002L7.48605 11.7891L7.48924 11.7865C8.18632 11.2056 8.53553 10.9146 8.9142 10.7964C9.36152 10.6567 9.84336 10.6788 10.2762 10.8584C10.6432 11.0106 10.9649 11.3324 11.6084 11.9759L11.6149 11.9823C12.2684 12.6358 12.596 12.9635 12.9688 13.1153C13.41 13.2951 13.9012 13.3106 14.3534 13.1606C14.7367 13.0334 15.0869 12.7275 15.7869 12.115L21.6326 7",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Chartup_default = Chartup;
var Player = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M12.5118 18.5197C12.5535 18.5196 12.6154 18.5191 12.6944 18.5187C12.8527 18.518 13.0812 18.5168 13.3595 18.5139C13.9161 18.5079 14.6722 18.4957 15.4718 18.4719C16.2728 18.448 17.1119 18.4125 17.835 18.3596C18.3929 18.3187 18.8465 18.2696 19.1563 18.216L19.4171 18.1613C19.8798 18.0372 20.2588 17.7 20.4415 17.2609L20.5079 17.0666C20.6745 16.4429 20.7699 15.2118 20.8184 14.0607C20.842 13.5013 20.8535 12.9851 20.8595 12.6086C20.8624 12.4206 20.8646 12.2678 20.8653 12.1623V11.8381C20.8646 11.7326 20.8624 11.5798 20.8595 11.3918C20.8535 11.0153 20.842 10.499 20.8184 9.93964C20.776 8.93262 20.698 7.86459 20.5675 7.1955L20.5079 6.93475V6.93378C20.3837 6.4676 20.0469 6.08808 19.6104 5.90546L19.4171 5.83905C19.1379 5.764 18.5792 5.69527 17.835 5.64081C17.1119 5.58789 16.2728 5.55237 15.4718 5.5285C14.6722 5.50468 13.9161 5.49248 13.3595 5.48651C13.0812 5.48353 12.8527 5.48237 12.6944 5.48163C12.6154 5.48126 12.5535 5.48074 12.5118 5.48065H12.3858C12.3441 5.48074 12.2823 5.48126 12.2032 5.48163C12.045 5.48237 11.8171 5.48353 11.5391 5.48651C10.9824 5.49248 10.2257 5.50467 9.42586 5.5285C8.62502 5.55237 7.7866 5.5879 7.06356 5.64081C6.50528 5.68166 6.05116 5.73075 5.74129 5.78436L5.48055 5.83905C5.01791 5.96308 4.63881 6.30032 4.45613 6.73944L4.38973 6.93378V6.93475L4.33113 7.1955C4.20063 7.86459 4.12165 8.93255 4.07918 9.93964C4.05559 10.499 4.04408 11.0153 4.03817 11.3918C4.03521 11.5798 4.03402 11.7326 4.03328 11.8381C4.03292 11.8907 4.0324 11.9317 4.03231 11.9592V12.0412C4.0324 12.0687 4.03292 12.1097 4.03328 12.1623C4.03402 12.2678 4.03521 12.4206 4.03817 12.6086C4.04408 12.9851 4.05559 13.5013 4.07918 14.0607C4.12165 15.0678 4.20064 16.1357 4.33113 16.8049L4.38973 17.0666C4.51389 17.5325 4.85085 17.9121 5.28719 18.0949L5.48055 18.1613C5.75983 18.2364 6.31924 18.3051 7.06356 18.3596C7.78661 18.4125 8.62502 18.448 9.42586 18.4719C10.2257 18.4957 10.9824 18.5079 11.5391 18.5139C11.8171 18.5168 12.045 18.518 12.2032 18.5187C12.2823 18.5191 12.3441 18.5196 12.3858 18.5197H12.5118Z",
            stroke: colorNameToHex(iconColor),
            strokeWidth: iconStroke
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M10.2615 14.2128C10.2615 14.596 10.6748 14.8368 11.0081 14.6478L14.9109 12.4352C15.2488 12.2437 15.2488 11.7569 14.911 11.5653L11.0081 9.35256C10.6748 9.16358 10.2615 9.40435 10.2615 9.78751V14.2128Z",
            fill: colorNameToHex(iconColor)
          }
        )
      ]
    }
  );
};
var Player_default = Player;
var Star = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M2.4166 10.3368C2.10335 10.0471 2.2735 9.52339 2.6972 9.47316L8.70078 8.76107C8.87346 8.74059 9.02345 8.63215 9.09628 8.47425L11.6285 2.98446C11.8072 2.59703 12.358 2.59695 12.5367 2.98439L15.0689 8.47413C15.1418 8.63204 15.2908 8.74077 15.4635 8.76124L21.4674 9.47316C21.8911 9.52339 22.0608 10.0472 21.7475 10.3369L17.3094 14.4419C17.1817 14.56 17.1249 14.7357 17.1588 14.9063L18.3366 20.8359C18.4198 21.2544 17.9744 21.5787 17.6021 21.3703L12.3268 18.4166C12.175 18.3317 11.9907 18.3321 11.839 18.417L6.56307 21.3695C6.19077 21.5779 5.74458 21.2544 5.82773 20.8359L7.00578 14.9066C7.03967 14.7361 6.98297 14.5599 6.8553 14.4419L2.4166 10.3368Z",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Star_default = Star;
var Chart = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M9.89797 11V20M9.89797 11H5.49757C4.93752 11 4.65777 11 4.44386 11.109C4.2557 11.2049 4.10283 11.3577 4.00696 11.5459C3.89796 11.7598 3.89796 12.04 3.89796 12.6001V20H9.89797M9.89797 11V5.6001C9.89797 5.04004 9.89797 4.75981 10.007 4.5459C10.1028 4.35774 10.2557 4.20487 10.4439 4.10899C10.6578 4 10.9375 4 11.4976 4H14.2976C14.8576 4 15.1382 4 15.3522 4.10899C15.5403 4.20487 15.6927 4.35774 15.7886 4.5459C15.8976 4.75981 15.898 5.04005 15.898 5.6001V8M9.89797 20H15.898M15.898 20L21.898 20.0001V9.6001C21.898 9.04005 21.8976 8.75981 21.7886 8.5459C21.6927 8.35774 21.5409 8.20487 21.3527 8.10899C21.1388 8 20.858 8 20.298 8H15.898M15.898 20V8",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Chart_default = Chart;
var Mail = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M4.71429 6L10.8219 10.6123L10.824 10.614C11.5021 11.1113 11.8414 11.3601 12.2131 11.4562C12.5415 11.5412 12.8868 11.5412 13.2153 11.4562C13.5872 11.36 13.9275 11.1105 14.6069 10.6123C14.6069 10.6123 18.5244 7.60594 20.7143 6M3.71429 15.8002V8.2002C3.71429 7.08009 3.71429 6.51962 3.93228 6.0918C4.12403 5.71547 4.42977 5.40973 4.80609 5.21799C5.23391 5 5.79438 5 6.91449 5H18.5145C19.6346 5 20.1939 5 20.6217 5.21799C20.998 5.40973 21.3048 5.71547 21.4965 6.0918C21.7143 6.5192 21.7143 7.07899 21.7143 8.19691V15.8036C21.7143 16.9215 21.7143 17.4805 21.4965 17.9079C21.3048 18.2842 20.998 18.5905 20.6217 18.7822C20.1943 19 19.6353 19 18.5174 19H6.9112C5.79329 19 5.2335 19 4.80609 18.7822C4.42977 18.5905 4.12403 18.2842 3.93228 17.9079C3.71429 17.4801 3.71429 16.9203 3.71429 15.8002Z",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Mail_default = Mail;
var Setting = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M20.8806 8.92293L20.5143 8.7192C20.4575 8.68756 20.4295 8.67169 20.402 8.65524C20.129 8.49165 19.8988 8.26564 19.7308 7.99523C19.7139 7.96802 19.698 7.93949 19.6654 7.8831C19.6329 7.82677 19.6164 7.79823 19.6013 7.76998C19.4506 7.48866 19.3691 7.17515 19.3642 6.85606C19.3637 6.82398 19.3638 6.79121 19.3649 6.72604L19.3721 6.30078C19.3835 5.62025 19.3893 5.27894 19.2936 4.97262C19.2087 4.70053 19.0666 4.44993 18.8768 4.23725C18.6623 3.99685 18.3653 3.82534 17.7708 3.48276L17.277 3.1982C16.6842 2.85658 16.3877 2.68571 16.0729 2.62057C15.7945 2.56294 15.5071 2.56561 15.2297 2.62789C14.9166 2.69819 14.6238 2.87351 14.0385 3.22396L14.0352 3.22555L13.6813 3.43741C13.6254 3.47091 13.5971 3.48779 13.569 3.50338C13.2907 3.6581 12.9801 3.74365 12.6619 3.75387C12.6298 3.7549 12.5972 3.7549 12.5319 3.7549C12.4671 3.7549 12.4331 3.7549 12.4011 3.75387C12.0821 3.74361 11.7708 3.65759 11.4921 3.50224C11.464 3.48658 11.4362 3.46956 11.3802 3.4359L11.0241 3.22213C10.4348 2.86836 10.1398 2.69121 9.82488 2.62057C9.54631 2.55807 9.25798 2.55634 8.97851 2.61471C8.66297 2.68062 8.36637 2.85276 7.77319 3.19703L7.77055 3.1982L7.28289 3.48124L7.27749 3.48454C6.68965 3.82572 6.39502 3.99672 6.18231 4.23614C5.99355 4.4486 5.85245 4.69881 5.76801 4.97018C5.67255 5.27691 5.67764 5.61896 5.68914 6.3027L5.69629 6.72736C5.69737 6.79166 5.69924 6.82362 5.69878 6.85525C5.69404 7.17499 5.61147 7.48914 5.46035 7.77096C5.44541 7.79883 5.42931 7.8267 5.39715 7.88237C5.36497 7.93809 5.34938 7.96579 5.3327 7.99268C5.16397 8.26452 4.93275 8.49186 4.65794 8.65572C4.63076 8.67193 4.60211 8.68752 4.54582 8.71871L4.18426 8.91908C3.58269 9.25245 3.28198 9.41928 3.06317 9.65669C2.86959 9.86672 2.72336 10.1158 2.6341 10.3872C2.5332 10.6939 2.53328 11.0378 2.53484 11.7255L2.53612 12.2877C2.53767 12.9708 2.5398 13.3122 2.64093 13.6168C2.7304 13.8863 2.87556 14.134 3.06805 14.3427C3.28562 14.5787 3.58335 14.7445 4.18035 15.0766L4.53869 15.276C4.59968 15.3099 4.63037 15.3266 4.65977 15.3444C4.93209 15.5083 5.1615 15.735 5.32879 16.0053C5.34686 16.0345 5.36421 16.0648 5.3989 16.1255C5.43317 16.1853 5.4507 16.2152 5.46655 16.2452C5.61322 16.5229 5.69175 16.8315 5.6971 17.1455C5.69768 17.1794 5.69719 17.2137 5.69602 17.2827L5.68914 17.6902C5.67756 18.3763 5.67251 18.7197 5.76853 19.0273C5.85348 19.2994 5.99545 19.55 6.18524 19.7627C6.39976 20.0031 6.69716 20.1745 7.29168 20.5171L7.78539 20.8015C8.37824 21.1432 8.67456 21.3138 8.9893 21.379C9.26774 21.4366 9.55525 21.4344 9.8327 21.3721C10.1463 21.3017 10.4401 21.1258 11.027 20.7743L11.3809 20.5625C11.4368 20.5289 11.4652 20.5121 11.4932 20.4965C11.7715 20.3418 12.0818 20.2558 12.4001 20.2456C12.4322 20.2446 12.4648 20.2446 12.53 20.2446C12.5954 20.2446 12.628 20.2446 12.6601 20.2456C12.9791 20.2559 13.2913 20.3422 13.57 20.4975C13.5945 20.5112 13.6191 20.526 13.6622 20.5519L14.0384 20.7777C14.6277 21.1315 14.9222 21.3081 15.2371 21.3788C15.5156 21.4413 15.8042 21.4438 16.0837 21.3855C16.3991 21.3196 16.6963 21.1471 17.2892 20.803L17.7842 20.5157C18.3724 20.1743 18.6673 20.0031 18.8801 19.7636C19.0689 19.5512 19.2102 19.3011 19.2946 19.0297C19.3894 18.7252 19.3837 18.3858 19.3723 17.7119L19.3649 17.2724C19.3638 17.2081 19.3637 17.1761 19.3642 17.1445C19.3689 16.8247 19.4501 16.5104 19.6013 16.2286C19.6162 16.2007 19.6324 16.1726 19.6645 16.1171C19.6966 16.0615 19.7133 16.0337 19.73 16.0068C19.8987 15.7349 20.1302 15.5074 20.405 15.3435C20.4318 15.3275 20.4595 15.3122 20.5144 15.2818L20.5163 15.2809L20.8779 15.0805C21.4794 14.7472 21.7807 14.5801 21.9995 14.3427C22.1931 14.1327 22.3391 13.8839 22.4284 13.6126C22.5287 13.3077 22.5279 12.9658 22.5264 12.2861L22.5251 11.7119C22.5235 11.0287 22.5227 10.6874 22.4216 10.3828C22.3321 10.1133 22.1861 9.86561 21.9936 9.65685C21.7763 9.42111 21.4781 9.25526 20.8823 8.92378L20.8806 8.92293Z",
            stroke: colorNameToHex(iconColor),
            strokeWidth: iconStroke,
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M8.53094 12C8.53094 14.2091 10.3218 16 12.5309 16C14.7401 16 16.5309 14.2091 16.5309 12C16.5309 9.79082 14.7401 7.99996 12.5309 7.99996C10.3218 7.99996 8.53094 9.79082 8.53094 12Z",
            stroke: colorNameToHex(iconColor),
            strokeWidth: iconStroke,
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        )
      ]
    }
  );
};
var Setting_default = Setting;
var Image = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M3.34697 17.0001C3.34692 16.9355 3.34692 16.8689 3.34692 16.8002V7.2002C3.34692 6.08009 3.34692 5.51962 3.56491 5.0918C3.75666 4.71547 4.0624 4.40973 4.43872 4.21799C4.86654 4 5.42701 4 6.54712 4H18.1471C19.2672 4 19.827 4 20.2548 4.21799C20.6311 4.40973 20.9374 4.71547 21.1292 5.0918C21.3469 5.5192 21.3469 6.07899 21.3469 7.19691V16.8031C21.3469 17.2881 21.3469 17.6679 21.3291 17.9774M3.34697 17.0001C3.34774 17.9884 3.36029 18.5058 3.56491 18.9074C3.75666 19.2837 4.0624 19.5905 4.43872 19.7822C4.86613 20 5.42592 20 6.54383 20H18.1505C19.2684 20 19.8274 20 20.2548 19.7822C20.6311 19.5905 20.9374 19.2837 21.1292 18.9074C21.2524 18.6654 21.3059 18.3813 21.3291 17.9774M3.34697 17.0001L8.1149 11.4375L8.11631 11.436C8.53919 10.9426 8.75098 10.6955 9.0022 10.6064C9.22286 10.5282 9.46379 10.53 9.68335 10.6113C9.93356 10.704 10.142 10.9539 10.5588 11.4541L13.23 14.6595C13.6159 15.1226 13.8099 15.3554 14.0455 15.4489C14.2534 15.5313 14.4826 15.5406 14.697 15.4773C14.9411 15.4053 15.156 15.1904 15.5857 14.7607L16.0828 14.2637C16.5203 13.8262 16.739 13.6076 16.9866 13.5361C17.2041 13.4734 17.4366 13.4869 17.6458 13.5732C17.8839 13.6716 18.0771 13.9124 18.4636 14.3955L21.3291 17.9774M21.3291 17.9774L21.3469 17.9996M15.3469 10C14.7946 10 14.3469 9.55228 14.3469 9C14.3469 8.44772 14.7946 8 15.3469 8C15.8992 8 16.3469 8.44772 16.3469 9C16.3469 9.55228 15.8992 10 15.3469 10Z",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Image_default = Image;
var User = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M20.1632 21C20.1632 18.2386 16.5815 16 12.1632 16C7.74496 16 4.16324 18.2386 4.16324 21M12.1632 13C9.40181 13 7.16324 10.7614 7.16324 8C7.16324 5.23858 9.40181 3 12.1632 3C14.9247 3 17.1632 5.23858 17.1632 8C17.1632 10.7614 14.9247 13 12.1632 13Z",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var User_default = User;
var Building = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M2.97955 20H4.97955M4.97955 20H14.9796M4.97955 20V6.2002C4.97955 5.08009 4.97955 4.51962 5.19754 4.0918C5.38929 3.71547 5.69503 3.40973 6.07135 3.21799C6.49917 3 7.05964 3 8.17975 3H11.7797C12.8999 3 13.4591 3 13.8869 3.21799C14.2633 3.40973 14.57 3.71547 14.7618 4.0918C14.9796 4.5192 14.9796 5.07899 14.9796 6.19691V12M14.9796 20H20.9796M14.9796 20V12M20.9796 20H22.9796M20.9796 20V12C20.9796 11.0681 20.9795 10.6024 20.8272 10.2349C20.6242 9.74481 20.2352 9.35523 19.7452 9.15224C19.3776 9 18.9112 9 17.9793 9C17.0474 9 16.5815 9 16.2139 9.15224C15.7239 9.35523 15.3348 9.74481 15.1318 10.2349C14.9796 10.6024 14.9796 11.0681 14.9796 12M7.97955 10H11.9796M7.97955 7H11.9796",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Building_default = Building;
var Plus = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M6.79587 12H12.7959M12.7959 12H18.7959M12.7959 12V18M12.7959 12V6",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Plus_default = Plus;
var Check = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M6.61218 12L10.8548 16.2426L19.3392 7.75732",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Check_default = Check;
var CheckBig = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M4.42853 12L9.37828 16.9497L19.9858 6.34326",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var CheckBig_default = CheckBig;
var AddPlusCircle = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M8.06122 12H12.0612M12.0612 12H16.0612M12.0612 12V16M12.0612 12V8M12.0612 21C7.09066 21 3.06122 16.9706 3.06122 12C3.06122 7.02944 7.09066 3 12.0612 3C17.0318 3 21.0612 7.02944 21.0612 12C21.0612 16.9706 17.0318 21 12.0612 21Z",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var AddPlusCircle_default = AddPlusCircle;
var Filter = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M20.8776 5.6001C20.8776 5.04005 20.8772 4.75981 20.7682 4.5459C20.6723 4.35774 20.5199 4.20487 20.3318 4.10899C20.1178 4 19.8372 4 19.2772 4H6.47717C5.91712 4 5.63737 4 5.42346 4.10899C5.2353 4.20487 5.08243 4.35774 4.98656 4.5459C4.87756 4.75981 4.87756 5.04005 4.87756 5.6001V6.33736C4.87756 6.58195 4.87756 6.70433 4.90519 6.81942C4.92969 6.92146 4.9702 7.01893 5.02502 7.1084C5.08685 7.20928 5.17347 7.29591 5.34631 7.46875L10.4091 12.5315C10.582 12.7044 10.668 12.7904 10.7298 12.8914C10.7847 12.9808 10.8257 13.0786 10.8502 13.1807C10.8776 13.2946 10.8776 13.4155 10.8776 13.6552V18.411C10.8776 19.2682 10.8776 19.6971 11.0581 19.9552C11.2158 20.1806 11.459 20.331 11.7311 20.3712C12.0427 20.4172 12.4263 20.2257 13.193 19.8424L13.993 19.4424C14.3141 19.2819 14.4742 19.2013 14.5915 19.0815C14.6952 18.9756 14.7745 18.8485 14.8229 18.7084C14.8775 18.5499 14.8776 18.37 14.8776 18.011V13.6626C14.8776 13.418 14.8776 13.2958 14.9052 13.1807C14.9297 13.0786 14.9702 12.9808 15.025 12.8914C15.0865 12.7911 15.1722 12.7053 15.3429 12.5347L15.3463 12.5315L20.4091 7.46875C20.582 7.2958 20.668 7.20932 20.7298 7.1084C20.7847 7.01893 20.8257 6.92146 20.8502 6.81942C20.8776 6.70551 20.8776 6.58444 20.8776 6.3448V5.6001Z",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Filter_default = Filter;
var CloudClose = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx("g", { "clip-path": "url(#clip0_364_933)", children: /* @__PURE__ */ jsx(
          "path",
          {
            d: "M10.6939 15L12.6939 13M12.6939 13L14.6939 11M12.6939 13L10.6939 11M12.6939 13L14.6939 15M23.6939 15C23.6939 12.7909 21.903 11 19.6939 11C19.6703 11 19.6471 11.0002 19.6236 11.0006C19.1386 7.60802 16.2206 5 12.6939 5C9.89726 5 7.4841 6.64004 6.36285 9.01082C3.75597 9.18144 1.69391 11.3498 1.69391 13.9999C1.69391 16.7613 3.93248 19.0001 6.69391 19.0001L19.6939 19C21.903 19 23.6939 17.2091 23.6939 15Z",
            stroke: colorNameToHex(iconColor),
            strokeWidth: iconStroke,
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ) }),
        /* @__PURE__ */ jsx("defs", { children: /* @__PURE__ */ jsx("clipPath", { id: "clip0_364_933", children: /* @__PURE__ */ jsx("rect", { width: "24", height: "24", fill: "white", transform: "translate(0.693909)" }) }) })
      ]
    }
  );
};
var CloudClose_default = CloudClose;
var CloudUpload = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx("g", { "clip-path": "url(#clip0_797_15258)", children: /* @__PURE__ */ jsx(
          "path",
          {
            d: "M12.5103 16V10M12.5103 10L9.51025 12M12.5103 10L15.5103 12M23.5103 15C23.5103 12.7909 21.7194 11 19.5103 11C19.4866 11 19.4635 11.0002 19.4399 11.0006C18.955 7.60802 16.0369 5 12.5103 5C9.7136 5 7.30044 6.64004 6.1792 9.01082C3.57232 9.18144 1.51025 11.3498 1.51025 13.9999C1.51025 16.7613 3.74883 19.0001 6.51025 19.0001L19.5103 19C21.7194 19 23.5103 17.2091 23.5103 15Z",
            stroke: colorNameToHex(iconColor),
            strokeWidth: iconStroke,
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ) }),
        /* @__PURE__ */ jsx("defs", { children: /* @__PURE__ */ jsx("clipPath", { id: "clip0_797_15258", children: /* @__PURE__ */ jsx("rect", { width: "24", height: "24", fill: "white", transform: "translate(0.510254)" }) }) })
      ]
    }
  );
};
var CloudUpload_default = CloudUpload;
var Alarm = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M15.3266 17V18C15.3266 19.6569 13.9835 21 12.3266 21C10.6697 21 9.3266 19.6569 9.3266 18V17M15.3266 17H9.3266M15.3266 17H18.9171C19.2996 17 19.4918 17 19.6467 16.9478C19.9426 16.848 20.1741 16.6156 20.2739 16.3198C20.3263 16.1643 20.3263 15.9715 20.3263 15.5859C20.3263 15.4172 20.3261 15.3329 20.3129 15.2524C20.288 15.1004 20.229 14.9563 20.1392 14.8312C20.0917 14.7651 20.0314 14.7048 19.9124 14.5858L19.5229 14.1963C19.3972 14.0706 19.3266 13.9001 19.3266 13.7224V10C19.3266 6.134 16.1926 2.99999 12.3266 3C8.46061 3.00001 5.3266 6.13401 5.3266 10V13.7224C5.3266 13.9002 5.25584 14.0706 5.13017 14.1963L4.74066 14.5858C4.62136 14.7051 4.56164 14.765 4.5141 14.8312C4.42425 14.9564 4.36475 15.1004 4.3398 15.2524C4.3266 15.3329 4.3266 15.4172 4.3266 15.586C4.3266 15.9715 4.3266 16.1642 4.37905 16.3197C4.47885 16.6156 4.7114 16.848 5.00726 16.9478C5.16216 17 5.35361 17 5.73615 17H9.3266",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Alarm_default = Alarm;
var XLg = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M21.9592 21L12.9593 12M12.9593 12L3.95929 3M12.9593 12L21.9593 3M12.9593 12L3.95929 21.0001",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var XLg_default = XLg;
var XSm = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M16.7756 16L12.7756 12M12.7756 12L8.77563 8M12.7756 12L16.7757 8M12.7756 12L8.77563 16",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var XSm_default = XSm;
var XMd = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M18.5919 18L12.592 12M12.592 12L6.59198 6M12.592 12L18.592 6M12.592 12L6.59198 18",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var XMd_default = XMd;
var Trash = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M14.4083 10V17M10.4083 10V17M6.40833 6V17.8C6.40833 18.9201 6.40833 19.4798 6.62631 19.9076C6.81806 20.2839 7.1238 20.5905 7.50012 20.7822C7.92753 21 8.48732 21 9.60524 21H15.2114C16.3293 21 16.8883 21 17.3157 20.7822C17.692 20.5905 17.9988 20.2839 18.1906 19.9076C18.4083 19.4802 18.4083 18.921 18.4083 17.8031V6M6.40833 6H8.40833M6.40833 6H4.40833M8.40833 6H16.4083M8.40833 6C8.40833 5.06812 8.40833 4.60241 8.56057 4.23486C8.76355 3.74481 9.15264 3.35523 9.6427 3.15224C10.0102 3 10.4764 3 11.4083 3H13.4083C14.3402 3 14.8061 3 15.1737 3.15224C15.6637 3.35523 16.053 3.74481 16.256 4.23486C16.4082 4.6024 16.4083 5.06812 16.4083 6M16.4083 6H18.4083M18.4083 6H20.4083",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Trash_default = Trash;
var CircleUp = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsxs("g", { clipPath: "url(#clip0_407_1478)", children: [
          /* @__PURE__ */ jsx(
            "rect",
            {
              x: "1.47467",
              y: "1.25",
              width: "21.5",
              height: "21.5",
              rx: "7.25",
              stroke: colorNameToHex(iconColor),
              strokeWidth: iconStroke
            }
          ),
          /* @__PURE__ */ jsx(
            "path",
            {
              d: "M17.725 8.56256L13.7059 12.6445C13.6337 12.7178 13.5972 12.7545 13.5649 12.7834C13.0429 13.2507 12.2535 13.2507 11.7314 12.7835C11.6991 12.7545 11.6623 12.7178 11.5901 12.6445C11.5179 12.5711 11.4818 12.5344 11.4495 12.5055C10.9274 12.0382 10.1377 12.0382 9.61562 12.5055C9.58338 12.5344 9.54737 12.5709 9.47549 12.6439L6.72504 15.4374M17.725 8.56256L17.7247 12.6874M17.725 8.56256H13.5998",
              stroke: colorNameToHex(iconColor),
              strokeWidth: iconStroke,
              strokeLinecap: "round",
              strokeLinejoin: "round"
            }
          )
        ] }),
        /* @__PURE__ */ jsx("defs", { children: /* @__PURE__ */ jsx("clipPath", { id: "clip0_407_1478", children: /* @__PURE__ */ jsx("rect", { width: "24", height: "24", fill: "white", transform: "translate(0.22467)" }) }) })
      ]
    }
  );
};
var CircleUp_default = CircleUp;
var Info = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M12.041 11V16M12.041 21C7.07045 21 3.04102 16.9706 3.04102 12C3.04102 7.02944 7.07045 3 12.041 3C17.0116 3 21.041 7.02944 21.041 12C21.041 16.9706 17.0116 21 12.041 21ZM12.0908 8V8.1L11.9912 8.1002V8H12.0908Z",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Info_default = Info;
var FolderEdit = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M21.4899 9.25V9.2C21.4899 8.0799 21.4899 7.51984 21.2719 7.09202C21.0801 6.7157 20.7742 6.40974 20.3979 6.21799C19.97 6 19.41 6 18.2899 6L3.48987 6M3.48987 6L3.48987 16.8C3.48987 17.9201 3.48987 18.4802 3.70786 18.908C3.8996 19.2843 4.20556 19.5903 4.58189 19.782C5.00971 20 5.56976 20 6.68987 20H7.48987M3.48987 6L3.48987 5.6C3.48987 5.03995 3.48987 4.75992 3.59886 4.54601C3.69474 4.35785 3.84772 4.20487 4.03588 4.10899C4.24979 4 4.52982 4 5.08987 4H9.82713C10.0717 4 10.194 4 10.3091 4.02763C10.4111 4.05213 10.5087 4.09253 10.5982 4.14736C10.6991 4.2092 10.7855 4.29568 10.9585 4.46863L12.4899 6M16.4899 14L18.4899 16M11.4899 21V18.5L18.9899 11L21.4899 13.5L13.9899 21H11.4899Z",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var FolderEdit_default = FolderEdit;
var SpeakerMax = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M19.1261 4.68646C20.1252 5.61815 20.9229 6.74466 21.4698 7.99651C22.0167 9.24836 22.3013 10.599 22.3061 11.9651C22.3109 13.3312 22.0357 14.6838 21.4975 15.9394C20.9593 17.1951 20.1696 18.3271 19.177 19.2657M16.3981 7.61188C16.9976 8.17089 17.4762 8.8468 17.8043 9.5979C18.1325 10.349 18.3033 11.1594 18.3061 11.9791C18.309 12.7987 18.1439 13.6103 17.821 14.3636C17.4981 15.117 17.0242 15.7963 16.4287 16.3594M7.78645 15.4069L9.46168 17.48C10.335 18.5607 10.7716 19.101 11.1542 19.1598C11.4853 19.2108 11.82 19.0924 12.0456 18.8447C12.3062 18.5585 12.3062 17.8638 12.3062 16.4743V7.52566C12.3062 6.13621 12.3062 5.44149 12.0456 5.1553C11.82 4.90755 11.4853 4.78923 11.1542 4.84015C10.7716 4.89897 10.335 5.43933 9.46168 6.52003L7.78645 8.59313C7.61003 8.81145 7.52182 8.92061 7.41267 8.99916C7.31597 9.06875 7.20762 9.1205 7.09272 9.15197C6.96302 9.1875 6.82267 9.1875 6.54198 9.1875H5.11865C4.36245 9.1875 3.98435 9.1875 3.67879 9.28844C3.07746 9.48709 2.60575 9.95881 2.4071 10.5601C2.30615 10.8657 2.30615 11.2438 2.30615 12C2.30615 12.7562 2.30615 13.1343 2.4071 13.4399C2.60575 14.0412 3.07746 14.5129 3.67879 14.7116C3.98435 14.8125 4.36245 14.8125 5.11865 14.8125H6.54198C6.82267 14.8125 6.96302 14.8125 7.09272 14.848C7.20762 14.8795 7.31597 14.9312 7.41267 15.0008C7.52182 15.0794 7.61003 15.1885 7.78645 15.4069Z",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var SpeakerMax_default = SpeakerMax;
var AlarmRing = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M15.9387 17V18C15.9387 19.6569 14.5956 21 12.9387 21C11.2819 21 9.93872 19.6569 9.93872 18V17M15.9387 17H9.93872M15.9387 17H19.5292C19.9117 17 20.1039 17 20.2588 16.9478C20.5547 16.848 20.7862 16.6156 20.886 16.3198C20.9384 16.1643 20.9384 15.9715 20.9384 15.5859C20.9384 15.4172 20.9382 15.3329 20.925 15.2524C20.9001 15.1004 20.8411 14.9563 20.7513 14.8312C20.7038 14.7651 20.6435 14.7048 20.5245 14.5858L20.135 14.1963C20.0093 14.0706 19.9387 13.9001 19.9387 13.7224V10C19.9387 6.134 16.8047 2.99999 12.9387 3C9.07273 3.00001 5.93872 6.13401 5.93872 10V13.7224C5.93872 13.9002 5.86796 14.0706 5.74229 14.1963L5.35278 14.5858C5.23349 14.7051 5.17376 14.765 5.12622 14.8312C5.03638 14.9564 4.97687 15.1004 4.95192 15.2524C4.93872 15.3329 4.93872 15.4172 4.93872 15.586C4.93872 15.9715 4.93872 16.1642 4.99117 16.3197C5.09097 16.6156 5.32352 16.848 5.61938 16.9478C5.77428 17 5.96574 17 6.34828 17H9.93872M18.9573 2.01367C20.3365 3.05299 21.423 4.43177 22.1111 6.01574M6.92069 2.01367C5.54147 3.05299 4.45492 4.43177 3.76685 6.01574",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var AlarmRing_default = AlarmRing;
var AlarmNoti = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M15.5713 17V18C15.5713 19.6569 14.2281 21 12.5713 21C10.9144 21 9.57129 19.6569 9.57129 18V17.0001M15.5713 17L9.57129 17.0001M15.5713 17H19.5713C20.1236 17 20.5713 16.5523 20.5713 16V15.4141C20.5713 15.1489 20.4658 14.8946 20.2783 14.707L19.7676 14.1963C19.6419 14.0706 19.5713 13.9 19.5713 13.7222V10C19.5713 9.82357 19.5649 9.64855 19.5518 9.4761M9.57129 17.0001L5.57129 17.0001C5.01901 17.0001 4.57129 16.5521 4.57129 15.9998V15.4141C4.57129 15.1489 4.67673 14.8949 4.86426 14.7073L5.375 14.1958C5.50068 14.0701 5.57129 13.9002 5.57129 13.7224V9.99998C5.57129 6.13401 8.70529 3 12.5713 3C13.2829 3 13.9697 3.10618 14.6167 3.30357M19.5518 9.4761C20.7581 8.7873 21.5713 7.48861 21.5713 6C21.5713 3.79086 19.7804 2 17.5713 2C16.4011 2 15.3482 2.50253 14.6167 3.30357M19.5518 9.4761C18.9678 9.80949 18.2918 10 17.5713 10C15.3622 10 13.5713 8.20914 13.5713 6C13.5713 4.9611 13.9674 4.0147 14.6167 3.30357M19.5518 9.4761C19.5518 9.47609 19.5518 9.4761 19.5518 9.4761ZM14.6167 3.30357C14.6172 3.30371 14.6177 3.30385 14.6181 3.304",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var AlarmNoti_default = AlarmNoti;
var FileDocument = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M9.38757 17H15.3876M9.38757 14H15.3876M13.3879 3.00087C13.2924 3 13.185 3 13.0623 3H8.58777C7.46766 3 6.90719 3 6.47937 3.21799C6.10305 3.40973 5.79731 3.71547 5.60556 4.0918C5.38757 4.51962 5.38757 5.08009 5.38757 6.2002V17.8002C5.38757 18.9203 5.38757 19.4801 5.60556 19.9079C5.79731 20.2842 6.10305 20.5905 6.47937 20.7822C6.90678 21 7.46658 21 8.58451 21L16.1907 21C17.3086 21 17.8676 21 18.295 20.7822C18.6713 20.5905 18.9781 20.2842 19.1698 19.9079C19.3876 19.4805 19.3876 18.9215 19.3876 17.8036V9.32568C19.3876 9.20302 19.3875 9.09553 19.3866 9M13.3879 3.00087C13.6734 3.00348 13.8533 3.01407 14.0258 3.05547C14.2298 3.10446 14.4254 3.18526 14.6044 3.29492C14.8061 3.41857 14.9794 3.59181 15.3251 3.9375L18.4506 7.06298C18.7965 7.40889 18.9684 7.58136 19.0921 7.78319C19.2018 7.96214 19.2829 8.15726 19.3319 8.36133C19.3733 8.53379 19.384 8.71454 19.3866 9M13.3879 3.00087L13.3876 5.80021C13.3876 6.92031 13.3876 7.48015 13.6056 7.90797C13.7973 8.2843 14.103 8.59048 14.4794 8.78223C14.9068 9 15.4666 9 16.5845 9H19.3866",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var FileDocument_default = FileDocument;
var Show = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M3.79066 13.7788C5.57009 15.5478 8.67339 17.9999 12.204 17.9999C15.7347 17.9999 18.8374 15.5478 20.6168 13.7788C21.0861 13.3123 21.3216 13.0782 21.471 12.6201C21.5776 12.2933 21.5776 11.7067 21.471 11.3799C21.3216 10.9218 21.0862 10.6877 20.6168 10.2211C18.8374 8.45208 15.7347 6 12.204 6C8.67339 6 5.57009 8.45208 3.79066 10.2211C3.32099 10.688 3.08614 10.9216 2.93665 11.3799C2.83003 11.7067 2.83003 12.2933 2.93665 12.6201C3.08614 13.0784 3.32099 13.3119 3.79066 13.7788Z",
            stroke: colorNameToHex(iconColor),
            strokeWidth: iconStroke,
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M10.2039 12C10.2039 13.1046 11.0993 14 12.2039 14C13.3084 14 14.2039 13.1046 14.2039 12C14.2039 10.8954 13.3084 10 12.2039 10C11.0993 10 10.2039 10.8954 10.2039 12Z",
            stroke: colorNameToHex(iconColor),
            strokeWidth: iconStroke,
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        )
      ]
    }
  );
};
var Show_default = Show;
var Chat = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M5.61975 19.9203L7.14371 18.7012L7.15492 18.6926C7.47263 18.4384 7.63295 18.3101 7.81182 18.2188C7.9723 18.1368 8.14342 18.0771 8.31995 18.0408C8.51892 18 8.72618 18 9.14221 18H17.8232C18.9411 18 19.5008 18 19.9282 17.7822C20.3045 17.5905 20.6106 17.2842 20.8024 16.9079C21.0201 16.4805 21.0201 15.9215 21.0201 14.8036V7.19691C21.0201 6.07899 21.0201 5.5192 20.8024 5.0918C20.6106 4.71547 20.3039 4.40973 19.9275 4.21799C19.4997 4 18.9404 4 17.8203 4H6.22034C5.10023 4 4.53976 4 4.11194 4.21799C3.73561 4.40973 3.42988 4.71547 3.23813 5.0918C3.02014 5.51962 3.02014 6.08009 3.02014 7.2002V18.6712C3.02014 19.7369 3.02014 20.2696 3.2386 20.5433C3.42859 20.7813 3.71658 20.9198 4.02112 20.9195C4.37129 20.9191 4.78758 20.5861 5.61975 19.9203Z",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Chat_default = Chat;
var Heart = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M12.6527 7.69425C10.6527 2.99982 3.65271 3.49982 3.65271 9.49985C3.65271 15.4999 12.6527 20.5 12.6527 20.5C12.6527 20.5 21.6527 15.4999 21.6527 9.49985C21.6527 3.49982 14.6527 2.99982 12.6527 7.69425Z",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Heart_default = Heart;
var FolderUpload = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M12.469 16V10M12.469 10L9.46899 12M12.469 10L15.469 12M3.46899 6V16.8C3.46899 17.9201 3.46899 18.4798 3.68698 18.9076C3.87873 19.2839 4.18447 19.5905 4.56079 19.7822C4.9882 20 5.54799 20 6.6659 20H18.2721C19.39 20 19.949 20 20.3764 19.7822C20.7527 19.5905 21.0595 19.2841 21.2512 18.9078C21.4692 18.48 21.4692 17.9199 21.4692 16.7998L21.4692 9.19978C21.4692 8.07967 21.4692 7.51962 21.2512 7.0918C21.0595 6.71547 20.7529 6.40973 20.3766 6.21799C19.9488 6 19.3891 6 18.269 6H12.469M3.46899 6H12.469M3.46899 6C3.46899 4.89543 4.36442 4 5.46899 4H9.14351C9.63269 4 9.87786 4 10.108 4.05526C10.3121 4.10425 10.5068 4.18526 10.6858 4.29492C10.8876 4.41857 11.0608 4.59182 11.4065 4.9375L12.469 6",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var FolderUpload_default = FolderUpload;
var FileUpload = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M12.2853 18V12M12.2853 12L9.28528 14M12.2853 12L15.2853 14M13.2853 3.00087C13.1898 3 13.0826 3 12.96 3H8.48547C7.36537 3 6.8049 3 6.37708 3.21799C6.00075 3.40973 5.69501 3.71547 5.50327 4.0918C5.28528 4.51962 5.28528 5.08009 5.28528 6.2002V17.8002C5.28528 18.9203 5.28528 19.4801 5.50327 19.9079C5.69501 20.2842 6.00075 20.5905 6.37708 20.7822C6.80448 21 7.36428 21 8.48222 21L16.0884 21C17.2063 21 17.7653 21 18.1927 20.7822C18.569 20.5905 18.8758 20.2842 19.0675 19.9079C19.2853 19.4805 19.2853 18.9215 19.2853 17.8036V9.32568C19.2853 9.20296 19.2853 9.09561 19.2843 9M13.2853 3.00087C13.5709 3.00347 13.7516 3.01385 13.9241 3.05526C14.1282 3.10425 14.3231 3.18526 14.5021 3.29492C14.7038 3.41857 14.8771 3.59182 15.2228 3.9375L18.3483 7.06298C18.6942 7.40889 18.8661 7.58136 18.9898 7.78319C19.0995 7.96214 19.1806 8.15726 19.2296 8.36133C19.271 8.53376 19.2816 8.71451 19.2843 9M13.2853 3.00087V5.8C13.2853 6.9201 13.2853 7.47977 13.5033 7.90759C13.695 8.28392 14.0008 8.59048 14.3771 8.78223C14.8045 9 15.3643 9 16.4822 9H19.2843M19.2843 9H19.2855",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var FileUpload_default = FileUpload;
var Tag = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M4.84947 7.64502L4.51825 11.2885C4.46919 11.8281 4.44446 12.1001 4.48936 12.3584C4.52949 12.5892 4.60962 12.8112 4.72652 13.0142C4.85786 13.2422 5.05199 13.4363 5.43803 13.8224L10.6135 18.9979C11.4006 19.7849 11.7943 20.1786 12.2495 20.3265C12.6512 20.4571 13.0846 20.4573 13.4863 20.3268C13.9429 20.1785 14.3398 19.7821 15.1318 18.9901L19.0916 15.0303C19.8836 14.2382 20.279 13.8424 20.4274 13.3857C20.5579 12.9841 20.5571 12.5511 20.4266 12.1495C20.2782 11.6928 19.8834 11.297 19.0914 10.505L13.9287 5.34229C13.5391 4.95272 13.3443 4.75792 13.1152 4.62598C12.9122 4.50908 12.6902 4.4286 12.4594 4.38848C12.199 4.3432 11.9245 4.36809 11.3759 4.41797L7.74605 4.74796C6.80129 4.83384 6.32862 4.87698 5.95894 5.08255C5.63302 5.26379 5.36433 5.53248 5.18309 5.8584C4.97854 6.22623 4.93588 6.69555 4.85085 7.63092L4.84947 7.64502Z",
            stroke: colorNameToHex(iconColor),
            strokeWidth: iconStroke,
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M9.81415 9.71297C10.2047 9.32244 10.2047 8.68928 9.81415 8.29876C9.42363 7.90823 8.79001 7.90823 8.39949 8.29876C8.00896 8.68928 8.00859 9.32229 8.39911 9.71282C8.78963 10.1033 9.42363 10.1035 9.81415 9.71297Z",
            stroke: colorNameToHex(iconColor),
            strokeWidth: iconStroke,
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        )
      ]
    }
  );
};
var Tag_default = Tag;
var Lock = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M10.1483 9H8.11804C6.99794 9 6.43747 9 6.00964 9.21799C5.63332 9.40973 5.32758 9.71547 5.13583 10.0918C4.91785 10.5196 4.91785 11.0801 4.91785 12.2002V17.8002C4.91785 18.9203 4.91785 19.4801 5.13583 19.9079C5.32758 20.2842 5.63332 20.5905 6.00964 20.7822C6.43705 21 6.99687 21 8.11478 21H17.7209C18.8389 21 19.3978 21 19.8252 20.7822C20.2016 20.5905 20.5083 20.2842 20.7001 19.9079C20.9178 19.4805 20.9178 18.9215 20.9178 17.8036V12.1969C20.9178 11.079 20.9178 10.5192 20.7001 10.0918C20.5083 9.71547 20.2016 9.40973 19.8252 9.21799C19.3974 9 18.8381 9 17.718 9H15.6868M10.1483 9H15.6868M10.1483 9C10.0209 9 9.91785 8.89668 9.91785 8.76923V6C9.91785 4.34315 11.261 3 12.9178 3C14.5747 3 15.9178 4.34315 15.9178 6V8.76923C15.9178 8.89668 15.8142 9 15.6868 9",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Lock_default = Lock;
var MonitorPlay = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M15.7341 20H9.73413M4.73413 13.8002V8.2002C4.73413 7.08009 4.73413 6.51962 4.95212 6.0918C5.14386 5.71547 5.4496 5.40973 5.82593 5.21799C6.25375 5 6.81422 5 7.93433 5H17.5343C18.6544 5 19.2137 5 19.6415 5.21799C20.0178 5.40973 20.3246 5.71547 20.5164 6.0918C20.7341 6.5192 20.7341 7.07899 20.7341 8.19691V13.8031C20.7341 14.921 20.7341 15.48 20.5164 15.9074C20.3246 16.2837 20.0178 16.5905 19.6415 16.7822C19.2141 17 18.6551 17 17.5372 17H7.93104C6.81312 17 6.25333 17 5.82593 16.7822C5.4496 16.5905 5.14386 16.2837 4.95212 15.9074C4.73413 15.4796 4.73413 14.9203 4.73413 13.8002ZM15.2341 11L10.7341 8V14L15.2341 11Z",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var MonitorPlay_default = MonitorPlay;
var Archive = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M7.15076 9H17.9508M7.15076 9C6.59071 9 6.30998 9 6.09607 9.10899C5.90791 9.20487 5.75504 9.35774 5.65917 9.5459C5.55017 9.75981 5.55017 10.04 5.55017 10.6001V15.8001C5.55017 16.9202 5.55017 17.4804 5.76816 17.9082C5.95991 18.2845 6.26565 18.5905 6.64197 18.7822C7.06938 19 7.62917 19 8.74708 19H16.3529C17.4708 19 18.0298 19 18.4572 18.7822C18.8335 18.5905 19.1407 18.2844 19.3324 17.9081C19.5502 17.4807 19.5502 16.9216 19.5502 15.8037V10.591C19.5502 10.037 19.5502 9.75865 19.4418 9.5459C19.3459 9.35774 19.1923 9.20487 19.0042 9.10899C18.7903 9 18.5108 9 17.9508 9M7.15076 9H5.52548C4.67639 9 4.25209 9 4.01111 8.85156C3.68953 8.65347 3.50719 8.29079 3.53943 7.91447C3.56361 7.63223 3.81634 7.29089 4.32264 6.60739C4.46907 6.40971 4.54231 6.31084 4.63194 6.23535C4.75145 6.1347 4.89327 6.06322 5.04529 6.02709C5.1593 6 5.28168 6 5.52771 6H19.5721C19.8181 6 19.9408 6 20.0548 6.02709C20.2069 6.06322 20.3486 6.1347 20.4681 6.23535C20.5578 6.31084 20.6313 6.40924 20.7777 6.60693C21.284 7.29042 21.5372 7.63218 21.5613 7.91442C21.5936 8.29074 21.4106 8.65347 21.089 8.85156C20.848 9 20.4227 9 19.5736 9H17.9508M10.5502 14H14.5502",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Archive_default = Archive;
var ArrorLeftRight = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M16.3667 13L19.3667 16M19.3667 16L16.3667 19M19.3667 16H5.3667M8.3667 11L5.3667 8M5.3667 8L8.3667 5M5.3667 8H19.3667",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var ArrorLeftRight_default = ArrorLeftRight;
var ArrowReload = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 25 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M10.183 16H5.18298V21M14.183 8H19.183V3M4.76599 9.0034C5.32668 7.61566 6.26542 6.41304 7.47553 5.53223C8.68565 4.65141 10.1198 4.12752 11.6127 4.02051C13.1056 3.9135 14.5977 4.2274 15.9211 4.92661C17.2445 5.62582 18.3442 6.68254 19.0971 7.97612M19.6005 14.9971C19.0398 16.3848 18.1011 17.5874 16.891 18.4682C15.6809 19.3491 14.2481 19.8723 12.7552 19.9793C11.2624 20.0863 9.76905 19.7725 8.44568 19.0732C7.12231 18.374 6.02181 17.3175 5.26892 16.0239",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var ArrowReload_default = ArrowReload;
var Home = ({ iconSize: iconSize2 = 24, iconColor = "commonBlack", iconStroke = 1.5 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M20.9993 17.6465V11.4051C20.9993 10.804 20.9988 10.5033 20.9257 10.2235C20.8609 9.97565 20.7546 9.74107 20.6107 9.52908C20.4483 9.28988 20.2225 9.09151 19.7701 8.69567L14.3701 3.97067C13.5302 3.23572 13.1102 2.86843 12.6376 2.72866C12.2211 2.6055 11.7772 2.6055 11.3607 2.72866C10.8885 2.86833 10.4691 3.23527 9.63043 3.9691L4.22864 8.69567C3.77624 9.09152 3.55056 9.28988 3.38818 9.52908C3.24428 9.74107 3.13713 9.97565 3.07236 10.2235C2.99927 10.5033 2.99927 10.804 2.99927 11.4051V17.6465C2.99927 18.6949 2.99927 19.2189 3.17054 19.6324C3.3989 20.1837 3.83663 20.6222 4.38794 20.8506C4.80142 21.0219 5.32561 21.0219 6.37398 21.0219C7.42235 21.0219 7.94711 21.0219 8.3606 20.8506C8.91191 20.6222 9.34952 20.1838 9.57788 19.6325C9.74915 19.219 9.74927 18.6948 9.74927 17.6464V16.5214C9.74927 15.2788 10.7566 14.2714 11.9993 14.2714C13.2419 14.2714 14.2493 15.2788 14.2493 16.5214V17.6464C14.2493 18.6948 14.2493 19.219 14.4205 19.6325C14.6489 20.1838 15.0866 20.6222 15.6379 20.8506C16.0514 21.0219 16.5756 21.0219 17.624 21.0219C18.6723 21.0219 19.1971 21.0219 19.6106 20.8506C20.1619 20.6222 20.5995 20.1837 20.8279 19.6324C20.9992 19.2189 20.9993 18.6949 20.9993 17.6465Z",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Home_default = Home;
var ArrowCaret = ({
  iconColor = "commonBlack",
  iconSize: iconSize2 = 24,
  iconStroke = 1.5,
  direction = "right"
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      style: {
        transform: direction === "right" ? "rotate(0deg)" : direction === "left" ? "rotate(180deg)" : direction === "up" ? "rotate(90deg)" : "rotate(-90deg)"
      },
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M11 9L14 12L11 15",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var ArrowCaret_default = ArrowCaret;
var Unfold = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5,
  type = "more"
}) => {
  if (type === "more")
    return /* @__PURE__ */ jsx(
      "svg",
      {
        width: iconSize2,
        height: iconSize2,
        viewBox: "0 0 25 24",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        children: /* @__PURE__ */ jsx(
          "path",
          {
            d: "M16.2449 15L12.2449 19L8.24487 15M8.24487 9L12.2449 5L16.2449 9",
            stroke: colorNameToHex(iconColor),
            strokeWidth: iconStroke,
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        )
      }
    );
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      style: {
        transform: type === "high" ? "rotate(0deg)" : "rotate(180deg)"
      },
      children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M8 9L12 5L16 9",
            stroke: colorNameToHex(iconColor),
            strokeWidth: iconStroke,
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            opacity: "0.22",
            d: "M16 15L12 19L8 15",
            stroke: colorNameToHex(iconColor),
            strokeWidth: iconStroke,
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        )
      ]
    }
  );
};
var Unfold_default = Unfold;
var MenuCustomIcon = ({ iconSize: iconSize2 = 24, iconColor = "labelStrong" }) => {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M0 6C0 2.68629 2.68629 0 6 0H18C21.3137 0 24 2.68629 24 6V18C24 21.3137 21.3137 24 18 24H6C2.68629 24 0 21.3137 0 18V6Z",
            fill: colorNameToHex(iconColor)
          }
        ),
        /* @__PURE__ */ jsx("rect", { x: "6", y: "6", width: "8.4", height: "3.2", rx: "1.2", fill: "white" }),
        /* @__PURE__ */ jsx("rect", { x: "6", y: "10.4", width: "12", height: "3.2", rx: "1.2", fill: "white" }),
        /* @__PURE__ */ jsx("rect", { x: "6", y: "14.8", width: "12", height: "3.2", rx: "1.2", fill: "white" })
      ]
    }
  );
};
var Menu_default = MenuCustomIcon;
var FireCustomIcon = ({ iconSize: iconSize2 = 24, iconColor = "labelStrong" }) => {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M0 6C0 2.68629 2.68629 0 6 0H18C21.3137 0 24 2.68629 24 6V18C24 21.3137 21.3137 24 18 24H6C2.68629 24 0 21.3137 0 18V6Z",
            fill: colorNameToHex(iconColor)
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M11.1429 4.87658C10.9101 5.08846 11.1659 5.32315 11.2123 5.6992C11.2355 5.92641 11.2201 6.30246 11.1813 6.52966C11.0033 7.46198 10.6084 7.96339 8.93613 9.36577C8.36321 9.85151 7.67416 10.5096 7.40318 10.8308C6.85349 11.4968 6.28831 12.5779 6.1025 13.3379C5.92443 14.0587 5.98637 15.3984 6.22637 16.0251C6.76058 17.4432 8.16965 18.6262 9.81099 19.0571C10.7091 19.2843 10.8562 19.0884 10.3065 18.4068C9.40066 17.2865 9.33098 16.033 10.082 14.5131C10.3839 13.902 11.282 12.8678 11.5143 12.8678C11.731 12.8678 11.8781 13.1028 11.8781 13.4397C11.8781 14.0743 12.2188 14.5366 13.3259 15.3905C13.953 15.8763 14.2395 16.4404 14.1776 17.0515C14.1311 17.5607 13.9066 18.0229 13.4963 18.4695C13.1711 18.8299 13.1246 18.9787 13.3104 19.1354C13.4111 19.2216 13.5195 19.2216 13.8756 19.1354C15.8266 18.6967 17.4215 17.1768 17.886 15.2965C18.0951 14.4661 18.0099 13.189 17.6848 12.2097C17.3673 11.2539 17.0344 10.7211 16.7402 10.6898C16.5467 10.6663 16.5079 10.7055 16.3454 11.0815C16.1595 11.4968 15.9578 12.0736 15.4782 12.006C15.2255 11.9704 15.0405 11.8771 14.9285 11.6378C14.8106 11.3857 14.9363 11.2147 15.0137 10.9248C15.1918 10.2197 15.246 9.09939 15.1298 8.53531C15.0137 7.93205 14.526 6.9684 14.0924 6.47482C13.473 5.76972 11.7852 4.87658 11.7852 4.87658C11.7852 4.87658 11.332 4.70444 11.1429 4.87658Z",
            fill: "white"
          }
        )
      ]
    }
  );
};
var Fire_default = FireCustomIcon;
var FolderCustomIcon = ({ iconSize: iconSize2 = 24, iconColor = "labelStrong" }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M8.6748 2.99997C9.11602 2.99997 9.50022 2.99377 9.87207 3.08298L10.0996 3.14743C10.3239 3.22045 10.5386 3.31939 10.7393 3.44235L10.8594 3.52048C11.1332 3.7115 11.3716 3.95751 11.6445 4.23044L12.4141 4.99997H17.7998C18.3433 4.99997 18.8117 4.9989 19.1953 5.03024C19.5904 5.06256 19.9832 5.13449 20.3613 5.32712L20.5684 5.44235C21.0377 5.73012 21.4208 6.14319 21.6728 6.63767L21.7402 6.78024C21.8835 7.11435 21.9415 7.45878 21.9697 7.80466C22.0011 8.18831 22 8.65665 22 9.20017V16.7998L21.9971 17.5576C21.9933 17.7903 21.9854 18.0036 21.9697 18.1953C21.9415 18.5409 21.8832 18.8849 21.7402 19.2187L21.6728 19.3613C21.4211 19.8555 21.0381 20.2694 20.5684 20.5576L20.3613 20.6728C19.9833 20.8655 19.5902 20.9374 19.1953 20.9697C18.8123 21.001 18.345 21 17.8027 21H6.19726C5.6548 21 5.18699 21.001 4.80371 20.9697C4.45827 20.9415 4.11416 20.8833 3.78027 20.7402L3.63769 20.6728C3.14321 20.4208 2.73015 20.0377 2.44238 19.5683L2.32715 19.3613C2.13453 18.9832 2.06259 18.5904 2.03027 18.1953C1.99893 17.8117 2 17.3433 2 16.7998V5.99997C2.00005 4.34316 3.34318 2.99997 5 2.99997H8.6748ZM14.707 10.2929C14.3165 9.90248 13.6835 9.90249 13.293 10.2929L12 11.5859L10.707 10.2929L10.6309 10.2246C10.2381 9.90428 9.65907 9.92688 9.29297 10.2929C8.92687 10.659 8.90431 11.2381 9.22461 11.6308L9.29297 11.707L10.5859 13L9.29297 14.2929C8.90247 14.6834 8.9025 15.3165 9.29297 15.707C9.68349 16.0975 10.3165 16.0975 10.707 15.707L12 14.414L13.293 15.707L13.3691 15.7754C13.7619 16.0957 14.3409 16.0731 14.707 15.707C15.0731 15.3409 15.0957 14.7619 14.7754 14.3691L14.707 14.2929L13.4141 13L14.707 11.707C15.0975 11.3165 15.0975 10.6834 14.707 10.2929Z",
          fill: colorNameToHex(iconColor)
        }
      )
    }
  );
};
var Folder_default = FolderCustomIcon;
var PlayCustomIcon = ({ iconSize: iconSize2 = 24, iconColor = "labelStrong" }) => {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M0 6C0 2.68629 2.68629 0 6 0H18C21.3137 0 24 2.68629 24 6V18C24 21.3137 21.3137 24 18 24H6C2.68629 24 0 21.3137 0 18V6Z",
            fill: colorNameToHex(iconColor)
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M7.8 15.9606C7.8 16.8756 8.78292 17.454 9.58275 17.0096L16.7118 13.0492C17.5348 12.592 17.5348 11.4085 16.7118 10.9513L9.5828 6.99049C8.78296 6.54611 7.8 7.12447 7.8 8.03946V15.9606Z",
            fill: "white"
          }
        )
      ]
    }
  );
};
var PlayIcon_default = PlayCustomIcon;
var StarCustomIcon = ({ iconSize: iconSize2 = 24, iconColor = "labelStrong" }) => {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M0 6C0 2.68629 2.68629 0 6 0H18C21.3137 0 24 2.68629 24 6V18C24 21.3137 21.3137 24 18 24H6C2.68629 24 0 21.3137 0 18V6Z",
            fill: colorNameToHex(iconColor)
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M11.462 4.69029C11.682 4.24434 12.318 4.24434 12.538 4.69029L14.3291 8.31936C14.4165 8.49645 14.5854 8.61919 14.7809 8.64759L18.7858 9.22953C19.2779 9.30105 19.4744 9.90583 19.1183 10.253L16.2203 13.0778C16.0789 13.2156 16.0144 13.4142 16.0478 13.6089L16.7319 17.5976C16.816 18.0877 16.3015 18.4615 15.8613 18.2301L12.2792 16.3469C12.1044 16.255 11.8956 16.255 11.7208 16.3469L8.13869 18.2301C7.69851 18.4615 7.18405 18.0877 7.26812 17.5976L7.95224 13.6089C7.98562 13.4142 7.92109 13.2156 7.77968 13.0778L4.8817 10.2529C4.52559 9.90583 4.72209 9.30105 5.21423 9.22953L9.21914 8.64759C9.41457 8.61919 9.58351 8.49645 9.67091 8.31936L11.462 4.69029Z",
            fill: "white"
          }
        )
      ]
    }
  );
};
var Star_default2 = StarCustomIcon;
var CopyIcon = ({
  iconSize: iconSize2 = 24,
  iconColor = "commonBlack",
  iconStroke = 1.5
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M8.99939 9V6.2002C8.99939 5.08009 8.99939 4.51962 9.21738 4.0918C9.40912 3.71547 9.71486 3.40973 10.0912 3.21799C10.519 3 11.0795 3 12.1996 3H17.7996C18.9197 3 19.4795 3 19.9073 3.21799C20.2836 3.40973 20.5899 3.71547 20.7816 4.0918C20.9996 4.51962 20.9996 5.07967 20.9996 6.19978V11.7998C20.9996 12.9199 20.9996 13.48 20.7816 13.9078C20.5899 14.2841 20.2833 14.5905 19.907 14.7822C19.4796 15 18.9204 15 17.8025 15H14.9994M8.99939 9H6.19958C5.07948 9 4.51901 9 4.09119 9.21799C3.71486 9.40973 3.40912 9.71547 3.21738 10.0918C2.99939 10.5196 2.99939 11.0801 2.99939 12.2002V17.8002C2.99939 18.9203 2.99939 19.4801 3.21738 19.9079C3.40912 20.2842 3.71486 20.5905 4.09119 20.7822C4.51859 21 5.07838 21 6.1963 21H11.8029C12.9209 21 13.4799 21 13.9073 20.7822C14.2836 20.5905 14.5899 20.2839 14.7816 19.9076C14.9994 19.4802 14.9994 18.921 14.9994 17.8031V15M8.99939 9H11.7996C12.9197 9 13.4794 9 13.9073 9.21799C14.2836 9.40973 14.5899 9.71547 14.7816 10.0918C14.9994 10.5192 14.9994 11.079 14.9994 12.1969L14.9994 15",
          stroke: colorNameToHex(iconColor),
          strokeWidth: iconStroke,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var Copy_default = CopyIcon;
var SortIcon = ({
  iconColor = "commonBlack",
  status = "unfold",
  iconSize: iconSize2
}) => {
  if (status === "unfold")
    return /* @__PURE__ */ jsxs(
      "svg",
      {
        width: iconSize2,
        height: iconSize2,
        viewBox: "0 0 24 24",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        children: [
          /* @__PURE__ */ jsx(
            "path",
            {
              opacity: "0.22",
              d: "M11.6464 5.35355L7.85355 9.14645C7.53857 9.46143 7.76165 10 8.20711 10L15.7929 10C16.2383 10 16.4614 9.46143 16.1464 9.14645L12.3536 5.35355C12.1583 5.15829 11.8417 5.15829 11.6464 5.35355Z",
              fill: "#0F0F10"
            }
          ),
          /* @__PURE__ */ jsx(
            "path",
            {
              opacity: "0.22",
              d: "M12.3536 18.6464L16.1464 14.8536C16.4614 14.5386 16.2383 14 15.7929 14H8.20711C7.76165 14 7.53857 14.5386 7.85355 14.8536L11.6464 18.6464C11.8417 18.8417 12.1583 18.8417 12.3536 18.6464Z",
              fill: "#0F0F10"
            }
          )
        ]
      }
    );
  if (status === "asc")
    return /* @__PURE__ */ jsx(
      "svg",
      {
        width: iconSize2,
        height: iconSize2,
        viewBox: "0 0 24 24",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        children: /* @__PURE__ */ jsx(
          "path",
          {
            d: "M11.6464 10.3536L7.85355 14.1464C7.53857 14.4614 7.76165 15 8.20711 15L15.7929 15C16.2383 15 16.4614 14.4614 16.1464 14.1464L12.3536 10.3536C12.1583 10.1583 11.8417 10.1583 11.6464 10.3536Z",
            fill: colorNameToHex(iconColor)
          }
        )
      }
    );
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M12.3536 14.6464L16.1464 10.8536C16.4614 10.5386 16.2383 10 15.7929 10H8.20711C7.76165 10 7.53857 10.5386 7.85355 10.8536L11.6464 14.6464C11.8417 14.8417 12.1583 14.8417 12.3536 14.6464Z",
          fill: colorNameToHex(iconColor)
        }
      )
    }
  );
};
var Sort_default = SortIcon;
var CheckCircleIcon = ({ iconColor = "commonBlack", iconSize: iconSize2 = 24 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 24 24",
      fill: "none",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          "fill-rule": "evenodd",
          "clip-rule": "evenodd",
          d: "M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21ZM16.5762 9.48014C16.8413 9.16193 16.7983 8.68901 16.4801 8.42383C16.1619 8.15866 15.689 8.20165 15.4238 8.51986L10.9496 13.8889L8.53033 11.4697C8.23744 11.1768 7.76256 11.1768 7.46967 11.4697C7.17678 11.7626 7.17678 12.2374 7.46967 12.5303L10.4697 15.5303C10.6187 15.6793 10.8235 15.7588 11.034 15.7492C11.2445 15.7397 11.4413 15.642 11.5762 15.4801L16.5762 9.48014Z",
          fill: colorNameToHex(iconColor)
        }
      )
    }
  );
};
var CheckCircle_default = CheckCircleIcon;
var CheckWavy = ({
  iconColor = "primaryNormal",
  iconSize: iconSize2 = 24
}) => {
  return /* @__PURE__ */ jsx("svg", { width: iconSize2, height: iconSize2, viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx(
    "path",
    {
      "fill-rule": "evenodd",
      "clip-rule": "evenodd",
      d: "M13.0079 3.91448C12.4361 3.3957 11.5639 3.39569 10.9921 3.91448L9.99119 4.82258C9.70036 5.08644 9.31766 5.22573 8.92526 5.21054L7.57481 5.15827C6.80332 5.1284 6.13517 5.68905 6.03061 6.45401L5.84759 7.79302C5.79441 8.18209 5.59077 8.53479 5.28042 8.77538L4.21231 9.60339C3.60211 10.0764 3.45066 10.9354 3.86227 11.5886L4.58277 12.732C4.79212 13.0642 4.86284 13.4653 4.77974 13.8491L4.49376 15.1699C4.33038 15.9245 4.76648 16.6799 5.50166 16.9157L6.78855 17.3284C7.16248 17.4484 7.47446 17.7102 7.6575 18.0576L8.28746 19.2532C8.64734 19.9363 9.46695 20.2346 10.1817 19.9427L11.4328 19.4317C11.7964 19.2832 12.2036 19.2832 12.5672 19.4317L13.8183 19.9427C14.5331 20.2346 15.3527 19.9363 15.7125 19.2532L16.3425 18.0576C16.5255 17.7102 16.8375 17.4484 17.2114 17.3284L18.4983 16.9157C19.2335 16.6799 19.6696 15.9245 19.5062 15.1699L19.2203 13.8491C19.1372 13.4653 19.2079 13.0642 19.4172 12.732L20.1377 11.5886C20.5493 10.9354 20.3979 10.0764 19.7877 9.60339L18.7196 8.77538C18.4092 8.53479 18.2056 8.18209 18.1524 7.79302L17.9694 6.45401C17.8648 5.68905 17.1967 5.1284 16.4252 5.15827L15.0747 5.21054C14.6823 5.22573 14.2996 5.08644 14.0088 4.82258L13.0079 3.91448ZM15.5303 10.5301C15.8232 10.2372 15.8232 9.76235 15.5303 9.46945C15.2374 9.17656 14.7625 9.17656 14.4696 9.46945L10.9999 12.9391L9.53028 11.4695C9.23738 11.1766 8.76251 11.1766 8.46962 11.4695C8.17672 11.7623 8.17672 12.2372 8.46962 12.5301L10.4696 14.5301C10.7625 14.823 11.2374 14.823 11.5303 14.5301L15.5303 10.5301Z",
      fill: colorNameToHex(iconColor)
    }
  ) });
};
var CheckWavy_default = CheckWavy;
var SearchDirectoryIcon = ({ iconSize: iconSize2 }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: iconSize2,
      height: iconSize2,
      viewBox: "0 0 17 17",
      fill: "none",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M13.1665 13.1667L11.1665 11.1667M7.16663 13.8334H5.29779C4.55251 13.8334 4.17931 13.8334 3.89437 13.6882C3.64349 13.5604 3.43966 13.3562 3.31183 13.1053C3.1665 12.8201 3.1665 12.4469 3.1665 11.7002V5.30021C3.1665 4.55347 3.1665 4.17983 3.31183 3.89461C3.43966 3.64373 3.64349 3.4399 3.89437 3.31207C4.17958 3.16675 4.55323 3.16675 5.29997 3.16675H11.7C12.4467 3.16675 12.8196 3.16675 13.1048 3.31207C13.3556 3.4399 13.5602 3.64373 13.688 3.89461C13.8332 4.17955 13.8332 4.55274 13.8332 5.29802V7.16688M9.49984 11.8334C8.21117 11.8334 7.1665 10.7887 7.1665 9.50008C7.1665 8.21142 8.21117 7.16675 9.49984 7.16675C10.7885 7.16675 11.8332 8.21142 11.8332 9.50008C11.8332 10.7887 10.7885 11.8334 9.49984 11.8334Z",
          stroke: "white",
          "stroke-width": "1.3",
          "stroke-linecap": "round",
          "stroke-linejoin": "round"
        }
      )
    }
  );
};
var SearchDirectory_default = SearchDirectoryIcon;

// src/components/Icons/index.tsx
var iconComponents = {
  search: Search_default,
  search2: SearchDirectory_default,
  checkSuccess: CheckSuccess_default,
  checkWarning: CheckWarning_default,
  checkDanger: CheckDanger_default,
  arrowChevronLg: ArrowChevronLg_default,
  arrowChevronM: ArrowChevronM_default,
  arrowLg: ArrowLg_default,
  arrowMd: ArrowMd_default,
  arrowSm: ArrowSm_default,
  flag: Flag_default,
  chartUp: Chartup_default,
  player: Player_default,
  star: Star_default,
  chart: Chart_default,
  mail: Mail_default,
  setting: Setting_default,
  image: Image_default,
  user: User_default,
  building: Building_default,
  plus: Plus_default,
  check: Check_default,
  checkBig: CheckBig_default,
  addPlusCircle: AddPlusCircle_default,
  filter: Filter_default,
  cloudClose: CloudClose_default,
  cloudUpload: CloudUpload_default,
  alarm: Alarm_default,
  xLg: XLg_default,
  xSm: XSm_default,
  xMd: XMd_default,
  trash: Trash_default,
  circleUp: CircleUp_default,
  info: Info_default,
  folderEdit: FolderEdit_default,
  speakerMax: SpeakerMax_default,
  alarmRing: AlarmRing_default,
  alarmNoti: AlarmNoti_default,
  fileDocument: FileDocument_default,
  show: Show_default,
  chat: Chat_default,
  heart: Heart_default,
  folderUpload: FolderUpload_default,
  fileUpload: FileUpload_default,
  tag: Tag_default,
  lock: Lock_default,
  monitorPlay: MonitorPlay_default,
  archive: Archive_default,
  arrowLeftRight: ArrorLeftRight_default,
  arrowReload: ArrowReload_default,
  home: Home_default,
  arrowCaret: ArrowCaret_default,
  unfold: Unfold_default,
  customMenu: Menu_default,
  customFire: Fire_default,
  customFolder: Folder_default,
  customPlay: PlayIcon_default,
  customStar: Star_default2,
  copy: Copy_default,
  sort: Sort_default,
  checkCircle: CheckCircle_default,
  checkWavy: CheckWavy_default
};
var Icon = ({ name, ...props }) => {
  const IconComponent = iconComponents[name];
  return React.createElement(IconComponent, props);
};
var Icons_default = Icon;

// src/utils/props.ts
var defaultProps = (props) => {
  return props;
};
var Text = ({
  children,
  size,
  color = "labelNormal",
  fontWeight = "regular",
  as = "p",
  textAlign = "left",
  ellipsis = false,
  width,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    _Text,
    {
      ...props,
      ...defaultProps(props),
      as,
      size,
      color,
      fontWeight,
      textAlign,
      ellipsis,
      width,
      children
    }
  );
};
var Text_default = Text;
var getFontWeight = (fontWeight) => {
  if (fontWeight === "bold") return 700;
  if (fontWeight === "medium") return 500;
  return 400;
};
var _Text = styled18.p`
  width: ${({ width }) => width ? typeof width === "number" ? `${width}px` : width : "fit-content"};
  font-size: ${({ theme, size }) => theme.typography[size].fontSize};
  line-height: ${({ theme, size }) => theme.typography[size].lineHeight};
  letter-spacing: ${({ theme, size }) => theme.typography[size].letterSpacing};
  height: fit-content;
  color: ${({ color }) => colorNameToHex(color)};
  font-weight: ${({ fontWeight }) => getFontWeight(fontWeight)};
  text-align: ${({ textAlign }) => textAlign};
  align-items: ${({ textAlign }) => textAlign === "center" ? "center" : "start"};

  ${({ ellipsis }) => {
  if (ellipsis)
    return css`
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      `;
}}
`;
var DEFAULT_SIZE = "medium";
var DEFAULT_COLOR = "primary";
var DEFAULT_KIND = "solid";
var ButtonStyleMap = {
  small: {
    padding: "2px 12px",
    height: "32px",
    borderRadius: "8px",
    font: "body3",
    fontWeight: "medium",
    iconSize: 16,
    gap: "6px"
  },
  medium: {
    padding: "4px 16px",
    height: "40px",
    borderRadius: "10px",
    font: "body3",
    fontWeight: "medium",
    iconSize: 20,
    gap: "8px"
  },
  large: {
    padding: "6px 24px",
    height: "48px",
    borderRadius: "12px",
    font: "body2",
    fontWeight: "bold",
    iconSize: 24,
    gap: "12px"
  }
};
var ButtonTextColorMap = {
  primary: "primaryNormal",
  neutral: "labelNeutral",
  danger: "accentRed",
  success: "accentGreen"
};
var ButtonBGColorMap = {
  solid: {
    primary: "primaryNormal",
    neutral: "fillStrong",
    danger: "accentRed",
    success: "accentGreen"
  },
  plain: {
    primary: "commonWhite",
    neutral: "commonWhite",
    danger: "commonWhite",
    success: "commonWhite"
  },
  soft: {
    primary: "primaryAlternative",
    neutral: "fillAssistive",
    danger: "accentRedAlternative",
    success: "accentGreenAlternative"
  },
  outline: {
    primary: "commonWhite",
    neutral: "commonWhite",
    danger: "commonWhite",
    success: "commonWhite"
  }
};
var OutlineBorderColorMap = {
  primary: "primaryNeutral",
  neutral: "lineNormal",
  danger: "accentRedNeutral",
  success: "accentGreenNeutral"
};
var ButtonHoverBGColorMap = {
  solid: {
    primary: "primaryStrong",
    neutral: "fillHeavy",
    danger: "accentRedStrong",
    success: "accentGreenStrong"
  },
  plain: {
    primary: "primaryAlternative",
    neutral: "fillAlternative",
    danger: "accentRedAlternative",
    success: "accentGreenAlternative"
  },
  soft: {
    primary: "primaryNeutral",
    neutral: "fillNormal",
    danger: "accentRedNeutral",
    success: "accentGreenNeutral"
  },
  outline: {
    primary: "primaryAssistive",
    neutral: "fillAssistive",
    danger: "accentRedAssistive",
    success: "accentGreenAssistive"
  }
};
var ButtonContainer = styled18.button`
  display: flex;
  align-items: center;
  justify-content: center;

  width: ${({ width }) => width ? typeof width === "number" ? `${width}px` : width : "fit-content"};
  min-width: fit-content;
  height: ${({ size }) => ButtonStyleMap[size ?? DEFAULT_SIZE].height};
  padding: ${({ size }) => ButtonStyleMap[size ?? DEFAULT_SIZE].padding};
  border-radius: ${({ size }) => ButtonStyleMap[size ?? DEFAULT_SIZE].borderRadius};

  gap: ${({ size }) => ButtonStyleMap[size ?? DEFAULT_SIZE].gap};

  &:disabled {
    cursor: not-allowed;
  }

  ${({ color, kind, theme }) => {
  if (kind === "solid") {
    return css`
        background-color: ${colorNameToHex(ButtonBGColorMap.solid[color ?? DEFAULT_COLOR])};
        &:hover {
          background-color: ${colorNameToHex(ButtonHoverBGColorMap.solid[color ?? DEFAULT_COLOR])};
        }
        &:disabled {
          background-color: ${theme.fill.fillNeutral};
        }
      `;
  }
  if (kind === "plain") {
    return css`
        background-color: ${colorNameToHex(ButtonBGColorMap.plain[color ?? DEFAULT_COLOR])};
        &:hover {
          background-color: ${colorNameToHex(ButtonHoverBGColorMap.plain[color ?? DEFAULT_COLOR])};
        }
        &:disabled {
          background-color: ${theme.common.commonWhite};
        }
      `;
  }
  if (kind === "soft")
    return css`
        background-color: ${colorNameToHex(ButtonBGColorMap.soft[color ?? DEFAULT_COLOR])};

        &:hover {
          background-color: ${colorNameToHex(ButtonHoverBGColorMap.soft[color ?? DEFAULT_COLOR])};
        }
        &:disabled {
          background-color: ${theme.fill.fillAlternative};
        }
      `;
  if (kind === "outline")
    return css`
        border: 1px solid
          ${colorNameToHex(OutlineBorderColorMap[color ?? DEFAULT_COLOR])};
        background-color: ${colorNameToHex(ButtonBGColorMap.outline[color ?? DEFAULT_COLOR])};
        &:hover {
          background-color: ${colorNameToHex(ButtonHoverBGColorMap.outline[color ?? DEFAULT_COLOR])};
        }

        &:disabled {
          border: 1px solid ${theme.line.lineNormal};
          background-color: ${theme.common.commonWhite};
      `;
}}
`;
function Button(props) {
  const {
    kind = DEFAULT_KIND,
    variant,
    size = DEFAULT_SIZE,
    disabled,
    color = DEFAULT_COLOR,
    onClick,
    children,
    ...restProps
  } = props;
  const textColor = useMemo(() => {
    if (disabled) return "labelDisable";
    if (kind === "solid") return "commonWhite";
    return ButtonTextColorMap[color];
  }, [kind, color, disabled]);
  return /* @__PURE__ */ jsxs(
    ButtonContainer,
    {
      ...props,
      onClick,
      disabled,
      type: restProps.type || "button",
      ...restProps,
      children: [
        variant === "leftIcon" && /* @__PURE__ */ jsx(Icons_default, { ...props.iconProps, iconSize: ButtonStyleMap[size].iconSize, iconColor: textColor }),
        /* @__PURE__ */ jsx(
          Text_default,
          {
            size: ButtonStyleMap[size].font,
            fontWeight: ButtonStyleMap[size].fontWeight,
            color: textColor,
            as: "span",
            children
          }
        ),
        variant === "rightIcon" && /* @__PURE__ */ jsx(Icons_default, { ...props.iconProps, iconSize: ButtonStyleMap[size].iconSize, iconColor: textColor })
      ]
    }
  );
}
var Button_default = Button;

// src/components/Checkbox/constant.ts
var CheckboxSizeMap = {
  small: { containerSize: 16, iconWidth: 8, iconHeight: 6, borderRadius: 4 },
  medium: { containerSize: 20, iconWidth: 10, iconHeight: 8, borderRadius: 6 },
  large: { containerSize: 24, iconWidth: 12, iconHeight: 10, borderRadius: 6 }
};
var CheckedIconConfig = {
  small: {
    width: 8,
    height: 8,
    viewBox: "0 0 8 8",
    path: "M0.799805 3.99998L3.1998 6.39998L7.1998 1.59998",
    strokeWidth: 1.3
  },
  medium: {
    width: 10,
    height: 8,
    viewBox: "0 0 10 8",
    path: "M1 4L4 7L9 1",
    strokeWidth: 1.5
  },
  large: {
    width: 12,
    height: 10,
    viewBox: "0 0 12 10",
    path: "M1.2002 5.00002L4.8002 8.60002L10.8002 1.40002",
    strokeWidth: 1.5
  }
};
var CheckedIcon = ({ size = "medium", disabled = false }) => {
  const config = CheckedIconConfig[size];
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: config.width,
      height: config.height,
      viewBox: config.viewBox,
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: config.path,
          stroke: disabled ? themes.label.labelDisable : themes.common.commonWhite,
          strokeWidth: config.strokeWidth,
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  );
};
var CheckedIcon_default = CheckedIcon;
var Checkbox = ({
  checked,
  disabled,
  onChange,
  size = "medium",
  checkedColor = "primaryNormal"
}) => {
  return /* @__PURE__ */ jsxs(
    CheckboxContainer,
    {
      size,
      checked,
      disabled,
      checkedColor,
      children: [
        /* @__PURE__ */ jsx("input", { type: "checkbox", onChange: () => !disabled && onChange(!checked) }),
        checked && /* @__PURE__ */ jsx(CheckedIcon_default, { size, disabled })
      ]
    }
  );
};
var Checkbox_default = Checkbox;
var CheckboxContainer = styled18.label`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: ${({ size }) => CheckboxSizeMap[size ?? "medium"].containerSize}px;
  width: ${({ size }) => CheckboxSizeMap[size ?? "medium"].containerSize}px;
  min-height: ${({ size }) => CheckboxSizeMap[size ?? "medium"].containerSize}px;
  height: ${({ size }) => CheckboxSizeMap[size ?? "medium"].containerSize}px;
  border: 1px solid ${({ theme }) => theme.line.lineNormal};
  border-radius: ${({ size }) => CheckboxSizeMap[size ?? "medium"].borderRadius}px;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.common.commonWhite};

  ${({ checked, theme, disabled, checkedColor }) => {
  if (disabled)
    return css`
        border: 1px solid ${theme.line.lineNeutral};
        background-color: ${theme.fill.fillAlternative};
      `;
  if (checked)
    return css`
        background-color: ${colorNameToHex(checkedColor ?? "primaryNormal")};
        border: none;
      `;
  return css`
      &:hover {
        background-color: ${theme.fill.fillAssistive};
      }
    `;
}}

  > input {
    display: none;
  }
`;
var sizeConfig = {
  small: {
    height: 32,
    font: "body3",
    borderRadius: 8,
    padding: "0 12px"
  },
  medium: {
    height: 40,
    font: "body2",
    borderRadius: 10,
    padding: "0 16px"
  },
  large: {
    height: 52,
    font: "body1",
    borderRadius: 12,
    padding: "0 16px"
  }
};
var InputContainer = styled18.label`
  width: ${({ width }) => width ? typeof width === "number" ? `${width}px` : width : "fit-content"};
  min-width: ${({ minWidth }) => minWidth ? `${minWidth}px` : "160px"};
  display: flex;
  align-items: center;
  gap: 10px;
  height: ${({ size }) => sizeConfig[size].height}px;
  border: 1px solid ${({ theme }) => theme.line.lineNormal};
  box-sizing: border-box;
  padding: 0 12px;
  background-color: ${({ theme }) => theme.common.commonWhite};
  border-radius: ${({ size }) => sizeConfig[size].borderRadius}px;

  color: ${({ theme }) => theme.label.labelNeutral};

  ${({ disabled, focused, isCompleted, isError, theme }) => {
  if (disabled)
    return css`
        border: 1px solid ${theme.line.lineNormal};
        > input {
          color: ${theme.label.labelDisable};
          &::placeholder {
            color: ${theme.label.labelDisable};
          }
        }
      `;
  if (isError && focused)
    return css`
        border: 1px solid ${theme.accent.accentRed};
      `;
  if (focused)
    return css`
        border: 1px solid ${theme.primary.primaryNormal};
        > input {
          color: ${theme.label.labelNormal};
        }
      `;
  if (isCompleted && isError)
    return css`
        border: 1px solid ${theme.accent.accentRed};
      `;
  if (isCompleted)
    return css`
        border: 1px solid ${theme.line.lineNormal};
      `;
}}

  ${({ disabled, theme, isError, focused }) => {
  if (!disabled && !focused)
    return css`
        &:hover {
          color: ${theme.label.labelNeutral};
          border: 1px solid
            ${isError ? theme.accent.accentRedNeutral : theme.primary.primaryNeutral};
        }
      `;
}}
  

  > input {
    font-size: ${({ size, theme }) => theme.typography[sizeConfig[size].font].fontSize};
    line-height: ${({ size, theme }) => theme.typography[sizeConfig[size].font].lineHeight};
    font-weight: 400;

    color: ${({ theme }) => theme.label.labelNeutral};

    &::placeholder {
      color: ${({ theme }) => theme.label.labelAssistive};
    }
  }
`;
var Input = styled18.input`
  border: none;
  outline: none;
  width: 100%;
  height: 100%;
`;
var Input2 = forwardRef(
  ({ placeholder, size, disabled = false, isError = false, width, ...props }, ref) => {
    const isControlled = "value" in props;
    const value = isControlled ? props.value : props.initialValue;
    const [focused, setFocused] = useState(false);
    const [inputValue, setInputValue] = useState(value);
    const onChangeInput = (e) => {
      if (isControlled) props.onChange(e);
      setInputValue(e.target.value);
    };
    return /* @__PURE__ */ jsx(
      InputContainer,
      {
        size,
        disabled,
        focused,
        isError,
        isCompleted: !!inputValue && !focused,
        width,
        children: /* @__PURE__ */ jsx(
          Input,
          {
            ...isControlled && { value },
            onChange: onChangeInput,
            placeholder,
            onFocus: () => setFocused(true),
            onBlur: () => setFocused(false),
            ref,
            ...props
          }
        )
      }
    );
  }
);
Input2.displayName = "Input";
var Input_default = Input2;
var Flex = ({ children, ...props }) => {
  const { as, direction, justifyContent, gap, wrap, alignItems, ...rest } = props;
  return /* @__PURE__ */ jsx(
    FlexContainer,
    {
      as,
      direction,
      justifyContent,
      gap,
      wrap,
      alignItems,
      ...rest,
      children
    }
  );
};
var Flex_default = Flex;
var FlexContainer = styled18("div")`
  display: flex;
  flex-direction: ${({ direction }) => direction};
  justify-content: ${({ justifyContent }) => justifyContent};
  align-items: ${({ alignItems }) => alignItems};
  gap: ${({ gap }) => typeof gap === "number" ? `${gap}px` : gap};
  flex-wrap: ${({ wrap }) => wrap};
`;
var TextSizeMap = {
  small: "body3",
  medium: "body2",
  large: "body1"
};
var LabelForm = forwardRef(
  ({ label, required, ...props }, ref) => {
    const isControlled = "value" in props;
    const value = isControlled ? props.value : props.initialValue;
    return /* @__PURE__ */ jsxs(Container, { direction: "column", gap: 10, alignItems: "flex-start", width: props.width, children: [
      /* @__PURE__ */ jsxs(Text_default, { size: TextSizeMap[props.size], color: "labelAlternative", children: [
        label,
        required && /* @__PURE__ */ jsx(Required, { children: "*" })
      ] }),
      /* @__PURE__ */ jsx(
        Input_default,
        {
          ...isControlled ? { value } : { defaultValue: value, ref },
          ...props,
          size: props.size
        }
      )
    ] });
  }
);
LabelForm.displayName = "LabelForm";
var Container = styled18(Flex_default)`
  width: ${({ width }) => width ? typeof width === "number" ? `${width}px` : width : "100%"};
`;
var Required = styled18.span`
  color: ${({ theme }) => theme.status.statusCautionary};
  margin-left: 4px;
`;
var LabelForm_default = LabelForm;
var iconSize = {
  small: 14,
  medium: 18,
  large: 20
};
var SearchInput = forwardRef(
  ({ size, disabled, placeholder, onEnter, width, minWidth, ...props }, ref) => {
    const [focused, setFocused] = useState(false);
    const isControlled = "value" in props;
    const value = isControlled ? props.value : props.initialValue;
    const [inputValue, setInputValue] = useState(value);
    const onChangeInput = (e) => {
      if (isControlled) props.onChange(e);
      setInputValue(e.target.value);
    };
    return /* @__PURE__ */ jsxs(
      InputContainer,
      {
        size,
        disabled: !!disabled,
        focused,
        isError: false,
        isCompleted: !!inputValue && !focused,
        width,
        minWidth,
        children: [
          /* @__PURE__ */ jsx(Icons_default, { name: "search", iconColor: "primaryNormal", iconSize: iconSize[size] }),
          /* @__PURE__ */ jsx(
            Input,
            {
              ...isControlled ? { value } : { ref },
              onChange: onChangeInput,
              placeholder,
              onFocus: () => setFocused(true),
              onBlur: () => setFocused(false),
              onKeyDown: (e) => e.key === "Enter" && onEnter?.(e),
              ...props
            }
          )
        ]
      }
    );
  }
);
SearchInput.displayName = "SearchInput";
var Search_default2 = SearchInput;
var SearchForm = forwardRef(
  ({ size, children, ...props }, ref) => {
    const isControlled = "value" in props;
    return /* @__PURE__ */ jsxs(Flex_default, { direction: "column", gap: 12, children: [
      /* @__PURE__ */ jsx(
        Search_default2,
        {
          size,
          ...props,
          ...isControlled ? { value: props.value } : { defaultValue: props.initialValue, ref }
        }
      ),
      children
    ] });
  }
);
SearchForm.displayName = "SearchForm";
var SearchForm_default = SearchForm;
var Grid = ({ children, ...props }) => {
  const {
    as,
    columns,
    rows,
    gap,
    justifyItems,
    alignItems,
    justifyContent,
    alignContent,
    ...rest
  } = props;
  return /* @__PURE__ */ jsx(
    GridContainer,
    {
      as,
      columns,
      rows,
      gap,
      justifyItems,
      alignItems,
      justifyContent,
      alignContent,
      ...defaultProps(rest),
      ...rest,
      children
    }
  );
};
var Grid_default = Grid;
var GridContainer = styled18("div")`
  display: grid;
  grid-template-columns: repeat(${({ columns }) => columns}, 1fr);
  grid-template-rows: repeat(${({ rows }) => rows}, 1fr);
  gap: ${({ gap }) => typeof gap === "number" ? `${gap}px` : gap};
  justify-items: ${({ justifyItems }) => justifyItems};
  align-items: ${({ alignItems }) => alignItems};
  justify-content: ${({ justifyContent }) => justifyContent};
  align-content: ${({ alignContent }) => alignContent};
`;
var blockSize = 10;
var Pagination = ({
  className,
  totalPage,
  currentPage = 1,
  onPageChange,
  disabled = false
}) => {
  const blockIndex = useMemo(() => Math.floor((currentPage - 1) / blockSize), [currentPage]);
  const blockStart = useMemo(() => blockIndex * blockSize + 1, [blockIndex]);
  const blockEnd = useMemo(
    () => Math.min(blockStart + blockSize - 1, totalPage),
    [blockStart, totalPage]
  );
  const isFirstBlock = blockIndex === 0;
  const lastBlockIndex = Math.max(0, Math.floor((totalPage - 1) / blockSize));
  const isLastBlock = blockIndex >= lastBlockIndex;
  const pages = useMemo(() => {
    const result = [];
    for (let i = blockStart; i <= blockEnd; i++) {
      result.push(i);
    }
    return result;
  }, [blockStart, blockEnd]);
  const handleGoFirst = () => {
    onPageChange(1);
  };
  const handleGoPrevBlock = () => {
    onPageChange(blockStart - blockSize);
  };
  const handleGoNextBlock = () => {
    onPageChange(blockStart + blockSize);
  };
  const handleGoLast = () => {
    onPageChange(totalPage);
  };
  return /* @__PURE__ */ jsxs(Nav, { alignItems: "center", gap: 6, className, $disabled: disabled, children: [
    /* @__PURE__ */ jsx(ArrowBtn, { $hidden: isFirstBlock, onClick: handleGoFirst, disabled, children: /* @__PURE__ */ jsxs(DoubleArrow, { children: [
      /* @__PURE__ */ jsx(Icons_default, { name: "arrowChevronLg", iconSize: 20, direction: "left", iconColor: "labelStrong" }),
      /* @__PURE__ */ jsx(Icons_default, { name: "arrowChevronLg", iconSize: 20, direction: "left", iconColor: "labelStrong" })
    ] }) }),
    /* @__PURE__ */ jsx(ArrowBtn, { $hidden: isFirstBlock, onClick: handleGoPrevBlock, disabled, children: /* @__PURE__ */ jsx(Icons_default, { name: "arrowChevronLg", iconSize: 24, direction: "left", iconColor: "labelStrong" }) }),
    /* @__PURE__ */ jsx(Flex_default, { alignItems: "center", gap: 6, children: pages.map((page) => /* @__PURE__ */ jsx(
      Item,
      {
        $isActive: currentPage === page,
        onClick: () => onPageChange(page),
        disabled,
        children: /* @__PURE__ */ jsx(Text_default, { size: "body3", fontWeight: "medium", children: page })
      },
      page
    )) }),
    /* @__PURE__ */ jsx(ArrowBtn, { $hidden: isLastBlock, onClick: handleGoNextBlock, disabled, children: /* @__PURE__ */ jsx(Icons_default, { name: "arrowChevronLg", iconSize: 24, direction: "right", iconColor: "labelStrong" }) }),
    /* @__PURE__ */ jsx(ArrowBtn, { $hidden: isLastBlock, onClick: handleGoLast, disabled, children: /* @__PURE__ */ jsxs(DoubleArrow, { children: [
      /* @__PURE__ */ jsx(Icons_default, { name: "arrowChevronLg", iconSize: 20, direction: "right", iconColor: "labelStrong" }),
      /* @__PURE__ */ jsx(Icons_default, { name: "arrowChevronLg", iconSize: 20, direction: "right", iconColor: "labelStrong" })
    ] }) })
  ] });
};
var Pagination_default = Pagination;
var Nav = styled18(Flex_default, {
  shouldForwardProp: (prop) => prop !== "$disabled"
})`
  ${({ $disabled }) => $disabled && "pointer-events: none;"}
`;
var Item = styled18("button", {
  shouldForwardProp: (prop) => prop !== "$isActive"
})`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: ${({ theme, $isActive }) => $isActive ? theme.fill.fillNeutral : "transparent"};

  &:hover {
    background-color: ${({ theme, $isActive }) => $isActive ? theme.fill.fillNeutral : theme.fill.fillAssistive};
  }
`;
var ArrowBtn = styled18("button", {
  shouldForwardProp: (prop) => prop !== "$hidden"
})`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  ${({ $hidden }) => $hidden && "visibility: hidden;"}

  &:hover {
    background-color: ${({ theme }) => theme.fill.fillAssistive};
  }
`;
var DoubleArrow = styled18.span`
  display: flex;
  align-items: center;

  & > svg:last-child {
    margin-left: -12px;
  }
`;
var Title = ({
  children,
  size,
  color = "labelNormal",
  as,
  fontWeight = "regular",
  textAlign = "left",
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    _Title,
    {
      ...props,
      ...defaultProps(props),
      size,
      color,
      as: as || "h1",
      fontWeight,
      textAlign,
      children
    }
  );
};
var Title_default = Title;
var getFontWeight2 = (fontWeight) => {
  if (fontWeight === "bold") return 700;
  if (fontWeight === "medium") return 500;
  return 400;
};
var _Title = styled18.h1`
  width: fit-content;
  font-size: ${({ theme, size }) => theme.typography[size].fontSize};
  line-height: ${({ theme, size }) => theme.typography[size].lineHeight};
  font-weight: ${({ fontWeight }) => getFontWeight2(fontWeight)};

  color: ${({ color }) => colorNameToHex(color)};

  text-align: ${({ textAlign }) => textAlign};
`;
var Popup = ({
  title,
  description,
  onClose,
  actionBtnText,
  onClickActionBtn,
  ...props
}) => {
  const onClickClose = useCallback(() => {
    onClose?.();
  }, [onClose]);
  const buttonType = props.variant !== "threeBtn" && props.type === "simple" ? "simple" : "vertical";
  const buttons = useMemo(() => {
    const btns = [
      /* @__PURE__ */ jsx(
        Button_default,
        {
          kind: buttonType === "vertical" ? "solid" : "plain",
          size: "large",
          color: buttonType === "simple" && props.variant === "oneBtn" ? "neutral" : "primary",
          onClick: onClickActionBtn,
          width: "100%",
          children: actionBtnText
        },
        actionBtnText
      )
    ];
    if (props.variant === "threeBtn") {
      btns.push(
        /* @__PURE__ */ jsx(
          Button_default,
          {
            kind: "outline",
            size: "large",
            color: "neutral",
            onClick: props.onClickSubBtn,
            width: "100%",
            children: props.subBtnText
          }
        )
      );
    }
    if (props.variant === "twoBtn" || props.variant === "threeBtn") {
      btns.push(
        /* @__PURE__ */ jsx(Button_default, { kind: "plain", size: "large", color: "neutral", onClick: onClickClose, width: "100%", children: props.cancelBtnText })
      );
    }
    if (buttonType === "simple") btns.reverse();
    return btns;
  }, [props, actionBtnText, buttonType, onClickActionBtn, onClickClose]);
  return /* @__PURE__ */ jsxs(
    PopupContainer,
    {
      direction: "column",
      gap: 40,
      type: props.variant === "threeBtn" ? "vertical" : props.type,
      children: [
        /* @__PURE__ */ jsxs(Flex_default, { direction: "column", gap: 10, alignItems: "center", children: [
          /* @__PURE__ */ jsx(Title_default, { size: "subTitle2", fontWeight: "bold", color: "labelStrong", as: "h2", children: title }),
          /* @__PURE__ */ jsx(Text_default, { size: "body2", color: "labelAlternative", children: description })
        ] }),
        /* @__PURE__ */ jsx(Flex_default, { direction: buttonType === "simple" ? "row" : "column", gap: 8, children: buttons.map((button, index) => /* @__PURE__ */ jsx(React.Fragment, { children: button }, index)) })
      ]
    }
  );
};
var Popup_default = Popup;
var PopupContainer = styled18(Flex_default)`
  width: 360px;
  padding: ${({ type }) => type === "simple" ? "40px 40px 20px 40px" : "40px"};
  background-color: ${({ theme }) => theme.common.commonWhite};
  border-radius: 20px;
  box-shadow:
    0px 2px 8px 0px rgba(0, 0, 0, 0.08),
    0px 12px 16px 0px rgba(0, 0, 0, 0.08);
`;
var RadioSizeMap = {
  small: { radioSize: "16px", circleSize: "8px" },
  medium: { radioSize: "20px", circleSize: "10px" },
  large: { radioSize: "24px", circleSize: "13px" }
};
var Radio = ({ id, name, checked, disabled, size = "medium", onChange }) => {
  return /* @__PURE__ */ jsxs(RadioContainer, { size, disabled: !checked && !!disabled, checked, children: [
    /* @__PURE__ */ jsx(
      "input",
      {
        type: "radio",
        id,
        name,
        checked,
        disabled,
        onChange
      }
    ),
    checked && /* @__PURE__ */ jsx(RadioCircle, { size })
  ] });
};
var Radio_default = Radio;
var RadioContainer = styled18.label`
  display: flex;
  align-items: center;
  justify-content: center;

  width: ${({ size }) => RadioSizeMap[size].radioSize};
  height: ${({ size }) => RadioSizeMap[size].radioSize};
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.line.lineNormal};
  cursor: pointer;

  ${({ disabled, theme, checked }) => {
  if (disabled)
    return css`
        cursor: not-allowed;
        border: 1px solid ${theme.line.lineNeutral};
        background-color: ${theme.fill.fillAlternative};
      `;
  if (!checked)
    return css`
        &:hover {
          background-color: ${theme.fill.fillAssistive};
        }
      `;
}}

  > input {
    display: none;
  }
`;
var RadioCircle = styled18.div`
  width: ${({ size }) => RadioSizeMap[size].circleSize};
  height: ${({ size }) => RadioSizeMap[size].circleSize};
  background-color: ${({ theme }) => theme.primary.primaryNormal};
  border-radius: 50%;
`;
var useRadio = ({ names, initialChecked = "" }) => {
  const [checked, setChecked] = useState(initialChecked);
  const handleChange = (e) => {
    const name = e.target.name;
    if (!names.includes(name)) return;
    setChecked(name);
  };
  return { checked, handleChange };
};
var useRadio_default = useRadio;
var DEFAULT_SEGMENTED_CONTROL_SIZE = "medium";
var DEFAULT_SEGMENTED_CONTROL_TYPE = "soft";
var DEFAULT_SEGMENTED_CONTROL_COLOR = "primary";
var SegmentedControl = ({
  size = DEFAULT_SEGMENTED_CONTROL_SIZE,
  type = DEFAULT_SEGMENTED_CONTROL_TYPE,
  color = DEFAULT_SEGMENTED_CONTROL_COLOR,
  options,
  selectedOption,
  onChange
}) => {
  return /* @__PURE__ */ jsx(SegmentedControlContainer, { size, type, color, children: options.map((option) => /* @__PURE__ */ jsx(
    SegmentedControlerItem,
    {
      option,
      isSelected: selectedOption === option,
      size,
      type,
      color,
      onChange: () => onChange(option)
    },
    option
  )) });
};
var SegmentedControlerItem = ({
  option,
  isSelected,
  type,
  size,
  color,
  onChange
}) => {
  const textColor = isSelected ? color === "primary" ? "primaryNormal" : "labelStrong" : "labelAlternative";
  return /* @__PURE__ */ jsx(SegmentedControlerItemWrapper, { isSelected, size, type, color, children: /* @__PURE__ */ jsx("button", { onClick: onChange, children: /* @__PURE__ */ jsx(Text_default, { size: TextSizeMap2[size], color: textColor, fontWeight: "medium", children: option }) }) });
};
var SegmentedControl_default = SegmentedControl;
var TextSizeMap2 = {
  small: "body4",
  medium: "body3",
  large: "body2"
};
var SegmentedControlSizeMap = {
  small: { height: "32px", padding: "4px", borderRadius: "8px" },
  medium: { height: "40px", padding: "6px", borderRadius: "12px" },
  large: { height: "48px", padding: "6px", borderRadius: "12px" }
};
var SegmentedControlContainer = styled18.ul`
  width: fit-content;
  display: flex;
  height: ${({ size }) => SegmentedControlSizeMap[size ?? DEFAULT_SEGMENTED_CONTROL_SIZE].height};
  padding: ${({ size }) => SegmentedControlSizeMap[size ?? DEFAULT_SEGMENTED_CONTROL_SIZE].padding};
  border-radius: ${({ size }) => SegmentedControlSizeMap[size ?? DEFAULT_SEGMENTED_CONTROL_SIZE].borderRadius};

  background-color: ${({ type, theme }) => type === "soft" ? theme.fill.fillAlternative : theme.common.commonWhite};

  border: ${({ type, theme }) => type === "outline" ? `1px solid ${theme.line.lineNeutral}` : "none"};
`;
var SegmentedControlItemSizeMap = {
  small: { borderRadius: "6px", height: "24px" },
  medium: { borderRadius: "8px", height: "28px" },
  large: { borderRadius: "8px", height: "36px" }
};
var SegmentedControlerItemWrapper = styled18.li`
  display: flex;
  align-items: center;
  border-radius: ${({ size }) => SegmentedControlItemSizeMap[size].borderRadius};
  padding: 0 8px;
  border: 1px solid
    ${({ theme, isSelected }) => isSelected ? theme.line.lineNeutral : "transparent"};

  ${({ isSelected, type, theme }) => {
  if (isSelected) {
    return css`
        background-color: ${type === "soft" ? theme.common.commonWhite : theme.fill.fillAssistive};
      `;
  }
}}

  &:hover {
    p {
      color: ${({ color, theme }) => color === "primary" ? theme.primary.primaryNormal : theme.label.labelStrong};
    }
  }
`;
var TabContext = createContext({
  tabs: [],
  selectedTab: "",
  variant: "text",
  size: "medium",
  onChangeTab: () => {
  }
});
var normalizeTab = (tab) => {
  if (typeof tab === "string") {
    return { label: tab };
  }
  return tab;
};
var TabProvider = ({
  children,
  selectedTab,
  tabs,
  variant,
  size,
  onChangeTab
}) => {
  const onChange = (tab) => {
    onChangeTab(tab);
  };
  const normalizedTabs = tabs.map((tab) => normalizeTab(tab));
  return /* @__PURE__ */ jsx(
    TabContext.Provider,
    {
      value: {
        selectedTab,
        tabs: normalizedTabs,
        variant,
        size,
        onChangeTab: (tab) => onChange(tab)
      },
      children
    }
  );
};
var TabsProvider_default = TabProvider;
var Tab = ({ children, isSelected, icon, onClickTab }) => {
  const { variant, size } = useContext(TabContext);
  if (variant === "box")
    return /* @__PURE__ */ jsxs(BoxTab, { onClick: onClickTab, selected: isSelected, size, children: [
      icon && /* @__PURE__ */ jsx(IconWrapper, { selected: isSelected, children: icon }),
      /* @__PURE__ */ jsx(
        Text_default,
        {
          size: BoxTabSizeMap[size].fontSize,
          color: isSelected ? "commonWhite" : "labelNormal",
          fontWeight: "medium",
          as: "span",
          children
        }
      )
    ] });
  return /* @__PURE__ */ jsxs(TextTab, { onClick: onClickTab, selected: isSelected, size, children: [
    icon && /* @__PURE__ */ jsx(IconWrapper, { selected: isSelected, children: icon }),
    /* @__PURE__ */ jsx(
      Text_default,
      {
        size: TextTabSizeMap[size].fontSize,
        color: isSelected ? "labelStrong" : "labelAssistive",
        fontWeight: "bold",
        as: "span",
        children
      }
    )
  ] });
};
var Tab_default = Tab;
var BoxTabSizeMap = {
  small: {
    height: "28px",
    fontSize: "body3"
  },
  medium: {
    height: "36px",
    fontSize: "body2"
  },
  large: {
    height: "44px",
    fontSize: "body1"
  }
};
var BoxTab = styled18.li`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 16px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.common.commonWhite};
  border: 1px solid ${({ theme }) => theme.line.lineNormal};
  height: ${({ size }) => BoxTabSizeMap[size]?.height};
  cursor: pointer;

  ${({ theme, selected }) => selected ? css`
          background-color: ${theme.fill.fillStrong};
          border: none;
        ` : css`
          &:hover {
            background-color: ${theme.fill.fillAssistive};
          }
        `}
`;
var TextTabSizeMap = {
  small: {
    fontSize: "body3",
    height: "32px"
  },
  medium: {
    fontSize: "body2",
    height: "40px"
  },
  large: {
    fontSize: "body1",
    height: "48px"
  }
};
var TextTab = styled18.li`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 16px;
  height: ${({ size }) => TextTabSizeMap[size]?.height};
  cursor: pointer;

  ${({ theme, selected }) => selected ? css`
          border-bottom: 1.5px solid ${theme.label.labelStrong};
        ` : css`
          &:hover {
            border-bottom: 1.5px solid ${theme.line.lineNormal};
            > span {
              color: ${theme.label.labelAlternative};
            }
          }
        `}
`;
var IconWrapper = styled18("span", {
  shouldForwardProp: (prop) => prop !== "selected"
})`
  display: flex;
  align-items: center;
  color: ${({ theme, selected }) => selected ? theme.label.labelStrong : theme.label.labelAssistive};
`;
var BoxTabGapMap = {
  small: "5px",
  medium: "6px",
  large: "6px"
};
var TabList = ({ className }) => {
  const { tabs, size, variant, selectedTab, onChangeTab } = useContext(TabContext);
  const gap = variant === "box" ? BoxTabGapMap[size] : "0px";
  return /* @__PURE__ */ jsx(TabListContainer, { className, children: /* @__PURE__ */ jsx(Flex_default, { direction: "row", gap, alignItems: "center", justifyContent: "flex-start", wrap: "wrap", children: tabs.map((tab) => /* @__PURE__ */ jsx(
    Tab_default,
    {
      isSelected: tab.label === selectedTab,
      onClickTab: () => onChangeTab(tab.label),
      icon: tab.icon,
      children: tab.label
    },
    tab.label
  )) }) });
};
var TabList_default = TabList;
var TabListContainer = styled18.nav`
  width: fit-content;
`;
var TabPanel = ({ value, children }) => {
  const { selectedTab } = useContext(TabContext);
  if (selectedTab !== value) return null;
  return /* @__PURE__ */ jsx("div", { children });
};
var TabPanel_default = TabPanel;
var Tabs = ({
  tabs,
  selectedTab,
  children,
  variant = "text",
  size = "medium",
  className,
  onChangeTab
}) => {
  return /* @__PURE__ */ jsxs(
    TabsProvider_default,
    {
      tabs,
      variant,
      selectedTab,
      onChangeTab,
      size,
      children: [
        /* @__PURE__ */ jsx(TabList_default, { className }),
        children
      ]
    }
  );
};
var Tabs_default = Tabs;

// src/components/Tabs/index.tsx
var TabsComponent = Tabs_default;
TabsComponent.Tab = Tab_default;
TabsComponent.TabList = TabList_default;
TabsComponent.Panel = TabPanel_default;
var Tabs_default2 = TabsComponent;
var TagTextColorMap = {
  primary: "primaryNormal",
  neutral: "labelNormal",
  sky: "accentSky",
  deepBlue: "accentDeepblue",
  purple: "accentPurple",
  pink: "accentPink",
  success: "statusPositive",
  warning: "statusCautionary",
  danger: "statusNegative"
};
var TagBackgroundColorMap = {
  primary: themes.primary.primaryNormal,
  neutral: themes.fill.fillStrong,
  sky: themes.accent.accentSky,
  deepBlue: themes.accent.accentDeepblue,
  purple: themes.accent.accentPurple,
  pink: themes.accent.accentPink,
  success: themes.status.statusPositive,
  warning: themes.status.statusCautionary,
  danger: themes.status.statusNegative
};
var TagSizeMap = {
  small: { fontSize: "body4", padding: "0 6px", height: "22px", borderRadius: "6px" },
  medium: { fontSize: "body3", padding: "0 8px", height: "26px", borderRadius: "8px" },
  large: { fontSize: "body2", padding: "0 8px", height: "32px", borderRadius: "8px" }
};
var Tag2 = ({
  children,
  color = "primary",
  size = "medium",
  type = "solid",
  className
}) => {
  if (type === "soft")
    return /* @__PURE__ */ jsx(SoftTag, { color, size, className, children: /* @__PURE__ */ jsx(Text_default, { size: TagSizeMap[size].fontSize, color: TagTextColorMap[color], fontWeight: "medium", as: "span", children }) });
  return /* @__PURE__ */ jsx(SolidTag, { color, size, className, children: /* @__PURE__ */ jsx(Text_default, { size: TagSizeMap[size].fontSize, color: "commonWhite", fontWeight: "medium", as: "span", children }) });
};
var Tag_default2 = Tag2;
var SoftTag = styled18.div`
  width: fit-content;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ color, size }) => css`
    // HEX 값에 opacity 정보 추가
    background-color: ${TagBackgroundColorMap[color] + "1F"};
    padding: ${TagSizeMap[size].padding};
    height: ${TagSizeMap[size].height};
    border-radius: ${TagSizeMap[size].borderRadius};
  `}
`;
var SolidTag = styled18.div`
  width: fit-content;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ color, size }) => css`
    background-color: ${TagBackgroundColorMap[color]};
    padding: ${TagSizeMap[size].padding};
    height: ${TagSizeMap[size].height};
    border-radius: ${TagSizeMap[size].borderRadius};
  `}
`;

// src/styles/utils/hexWithOpacity.ts
var hexToRgba = (hex, opacity) => {
  const hexColor = hex.replace("#", "");
  const r = parseInt(hexColor.substring(0, 2), 16);
  const g = parseInt(hexColor.substring(2, 4), 16);
  const b = parseInt(hexColor.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};
var hexWithOpacity = (color, opacity) => {
  return hexToRgba(
    colorNameToHex(color),
    typeof opacity === "number" ? opacity : themes.opacity[opacity]
  );
};
var ToastContainer = () => {
  const { toastPopup } = useContext(ToastPopupContext);
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  if (!isMounted) return null;
  return createPortal(
    /* @__PURE__ */ jsx(ToastContainerWrapper, { id: "toast-container", direction: "column", gap: "10px", children: toastPopup.map((toast) => /* @__PURE__ */ jsx(ToastPopup_default, { ...toast }, toast.id)) }),
    document.body
  );
};
var ToastContainer_default = ToastContainer;
var ToastContainerWrapper = styled18(Flex_default)`
  position: fixed;
  left: 50%;
  height: fit-content;
  transform: translateX(-50%);
  z-index: 1000;
  bottom: 60px;
`;
var ToastPopupContext = createContext({
  toastPopup: [],
  setToastPopup: () => {
  },
  addToastPopup: () => {
  },
  removeToastPopup: () => {
  }
});
var useToastPopup = () => useContext(ToastPopupContext);
var ToastPopupProvider = ({ children }) => {
  const [toastPopup, setToastPopup] = useState([]);
  const initToastPopup = (toasts) => {
    return toasts.map((toast) => ({ ...toast, id: uniqueId() }));
  };
  const addToastPopup = (toastPopup2) => {
    const newToast = { ...toastPopup2, id: uniqueId() };
    setToastPopup((prev) => [...prev, newToast]);
  };
  const removeToastPopup = (id) => {
    setToastPopup((prev) => prev.filter((toast) => toast.id !== id));
  };
  return /* @__PURE__ */ jsxs(
    ToastPopupContext.Provider,
    {
      value: { toastPopup, setToastPopup: initToastPopup, addToastPopup, removeToastPopup },
      children: [
        /* @__PURE__ */ jsx(ToastContainer_default, {}),
        children
      ]
    }
  );
};
var ToastPopup = ({ message, status, ...props }) => {
  const { removeToastPopup } = useToastPopup();
  const iconName = status === "positive" ? "checkSuccess" : status === "warning" ? "checkWarning" : "checkDanger";
  const onClickClose = () => {
    removeToastPopup(props.id);
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      removeToastPopup(props.id);
    }, 3e3);
    return () => clearTimeout(timer);
  }, [props.id, removeToastPopup]);
  return /* @__PURE__ */ jsxs(ToastPopupContainer, { alignItems: "center", gap: 40, type: props.type, as: "label", children: [
    /* @__PURE__ */ jsxs(Flex_default, { alignItems: "center", gap: 12, children: [
      /* @__PURE__ */ jsx(Icons_default, { name: iconName }),
      /* @__PURE__ */ jsx(Text_default, { fontWeight: "medium", size: "body2", color: "commonWhite", children: message })
    ] }),
    props.type === "button" && /* @__PURE__ */ jsxs("button", { type: "button", onClick: props.onClick, className: "button", children: [
      /* @__PURE__ */ jsx(Text_default, { fontWeight: "medium", size: "body2", color: "commonWhite", children: props.text }),
      /* @__PURE__ */ jsx(Icons_default, { name: "arrowChevronM", iconSize: 24, iconColor: "commonWhite", direction: "right" })
    ] }),
    props.type === "close" && /* @__PURE__ */ jsx("button", { type: "button", onClick: onClickClose, children: /* @__PURE__ */ jsx(Icons_default, { name: "xSm", iconSize: 24, iconColor: "commonWhite" }) })
  ] });
};
var ToastPopup_default = ToastPopup;
var ToastPopupContainer = styled18(Flex_default)`
  width: fit-content;
  padding: 20px;
  background-color: ${hexWithOpacity("fillStrong", "opacity74")};
  border-radius: 16px;

  ${({ type }) => type !== "normal" && css`
      &:hover {
        background-color: ${hexWithOpacity("fillStrong", "opacity88")};
      }
    `}

  &:hover {
    cursor: pointer;
  }

  > button {
    display: flex;
    align-items: center;
    height: fit-content;
  }

  .button > p {
    color: ${hexWithOpacity("commonWhite", "opacity74")};
  }
  .button > svg > path {
    stroke: ${hexWithOpacity("commonWhite", "opacity74")};
  }
`;
var TOOLTIP_ICON_WIDTH = 17;
var TOOLTIP_ICON_HEIGHT = 12;
var TooltipArrowIcon = ({ color }) => {
  const iconColor = color === "white" ? "commonWhite" : color === "neutral" ? "fillStrong" : "primaryNormal";
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: TOOLTIP_ICON_WIDTH,
      height: TOOLTIP_ICON_HEIGHT,
      viewBox: "0 0 17 12",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx("g", { id: "Arrow", children: /* @__PURE__ */ jsx(
        "path",
        {
          id: "Polygon 1",
          d: "M9.81603 10.848C9.41749 11.4106 8.58252 11.4106 8.18398 10.848L0.5 1.21137e-06L17.5 0L9.81603 10.848Z",
          fill: colorNameToHex(iconColor)
        }
      ) })
    }
  );
};
var TooltipArrowIcon_default = TooltipArrowIcon;
var useIsMounted = () => {
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  return isMounted;
};
var COMPONENT_GAP = 12;
var PLACEMENT_MARGIN_POSITION_MAP = {
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left"
};
var Tooltip = ({
  children,
  content,
  placement = "top",
  initialIsOpened = false,
  onOpenChange,
  color = "neutral",
  trigger = "click",
  ...props
}) => {
  const [isOpened, setIsOpened] = useState(initialIsOpened);
  const isMounted = useIsMounted();
  const triggerRef = useRef(null);
  const popperRef = useRef(null);
  const { styles, attributes, update } = usePopper(triggerRef.current, popperRef.current, {
    placement
  });
  const isIncludeActionBtn = props.variant === "closeActionBtn" || props.variant === "actionBtn";
  const isIncludeCloseBtn = props.variant === "closeBtn" || props.variant === "closeActionBtn";
  const onClickCloseBtn = () => {
    setIsOpened?.(false);
  };
  useEffect(() => {
    if (isOpened && update) update();
  }, [isOpened, update]);
  useEffect(() => {
    onOpenChange?.(isOpened);
  }, [isOpened, onOpenChange]);
  if (!isMounted) {
    return /* @__PURE__ */ jsx(TooltipWrapper, { children: /* @__PURE__ */ jsx(TooltipTriggerBtn, { children }) });
  }
  return /* @__PURE__ */ jsxs(TooltipWrapper, { children: [
    createPortal(
      /* @__PURE__ */ jsx(
        TooltipContainer,
        {
          ref: popperRef,
          id: "tooltip-container",
          color,
          placement,
          style: styles.popper,
          ...attributes.popper,
          isOpened: !!isOpened,
          children: isOpened && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsxs(TooltipContent, { children: [
              typeof content === "string" ? /* @__PURE__ */ jsx(Text_default, { size: "body2", color: color === "white" ? "labelNeutral" : "commonWhite", as: "span", children: /* @__PURE__ */ jsx("pre", { children: content }) }) : content,
              isIncludeCloseBtn && /* @__PURE__ */ jsx(TooltipCloseBtn, { onClick: onClickCloseBtn, color, children: /* @__PURE__ */ jsx(
                Icons_default,
                {
                  name: "xMd",
                  iconSize: 20,
                  iconColor: color === "white" ? "labelAssistive" : "commonWhite"
                }
              ) })
            ] }),
            isIncludeActionBtn && /* @__PURE__ */ jsxs(TooltipActionBtn, { onClick: props.onClickActionBtn, color, children: [
              /* @__PURE__ */ jsx(Text_default, { size: "body2", color: color === "white" ? "labelAssistive" : "commonWhite", children: props.actionBtnText }),
              /* @__PURE__ */ jsx(
                Icons_default,
                {
                  name: "arrowChevronM",
                  iconSize: 20,
                  iconColor: color === "white" ? "labelAssistive" : "commonWhite"
                }
              )
            ] }),
            /* @__PURE__ */ jsx(TooltipArrowIcon_default, { color })
          ] })
        }
      ),
      document.getElementById("tooltip-root")
    ),
    /* @__PURE__ */ jsx(
      TooltipTriggerBtn,
      {
        onClick: () => trigger === "click" && setIsOpened?.(true),
        onMouseEnter: () => trigger === "hover" && setIsOpened?.(true),
        onMouseLeave: () => trigger === "hover" && setIsOpened?.(false),
        ref: triggerRef,
        children
      }
    )
  ] });
};
var Tooltip_default = Tooltip;
var TooltipWrapper = styled18.div`
  position: relative;
  width: fit-content;
`;
var TooltipContainer = styled18.div`
  width: auto;
  height: fit-content;
  z-index: 9999999;

  display: ${({ isOpened }) => isOpened ? "block" : "none"};

  white-space: nowrap;

  padding: 16px;
  border-radius: 12px;

  background-color: ${({ color, theme }) => color === "white" ? theme.common.commonWhite : color === "neutral" ? theme.fill.fillStrong : theme.primary.primaryNormal};

  box-shadow: ${({ theme }) => theme.shadow.shadowL};

  ${({ placement }) => css`
        margin-${PLACEMENT_MARGIN_POSITION_MAP[placement]}: ${TOOLTIP_ICON_HEIGHT + COMPONENT_GAP}px;
      `}

  &:hover {
    button {
      opacity: 1;

      path {
        stroke: ${({ theme, color }) => color === "white" && theme.label.labelStrong};
      }
      p {
        color: ${({ theme, color }) => color === "white" && theme.label.labelStrong};
      }
    }
  }

  > svg {
    position: absolute;
    ${({ placement }) => {
  if (placement === "bottom") {
    return css`
          left: 50%;
          transform: translateX(-50%) rotate(180deg);
          top: -12px;
        `;
  }
  if (placement === "top") {
    return css`
          left: 50%;
          transform: translateX(-50%);
          bottom: -12px;
        `;
  }
  if (placement === "left") {
    return css`
          right: -14px;
          top: 50%;
          transform: translateY(-50%) rotate(-90deg);
        `;
  }
  if (placement === "right") {
    return css`
          left: -14px;
          top: 50%;
          transform: translateY(-50%) rotate(90deg);
        `;
  }
}}
  }
`;
var TooltipContent = styled18.div`
  display: flex;
  gap: 20px;
`;
var TooltipActionBtn = styled18.button`
  display: flex;
  align-items: center;
  opacity: ${({ theme, color }) => color === "white" ? "1" : theme.opacity.opacity52};
`;
var TooltipCloseBtn = styled18.button`
  opacity: ${({ theme, color }) => color === "white" ? "1" : theme.opacity.opacity61};
`;
var TooltipTriggerBtn = styled18.button`
  display: flex;
`;
var CollableThemeProvider = ({ children }) => {
  return /* @__PURE__ */ jsx(ThemeProvider, { theme: themes, children });
};
var CollableThemeProvider_default = CollableThemeProvider;
var SizeMap = {
  s: {
    width: 24,
    borderRadius: 6,
    iconSize: 12,
    lockOverlay: 18,
    lockIcon: 11
  },
  sm: {
    width: 28,
    borderRadius: 8,
    iconSize: 14,
    lockOverlay: 18,
    lockIcon: 11
  },
  m: {
    width: 32,
    borderRadius: 8,
    iconSize: 16,
    lockOverlay: 18,
    lockIcon: 11
  },
  ml: {
    width: 36,
    borderRadius: 10,
    iconSize: 18,
    lockOverlay: 18,
    lockIcon: 11
  },
  l: {
    width: 40,
    borderRadius: 12,
    iconSize: 20,
    lockOverlay: 20,
    lockIcon: 12
  },
  xl: {
    width: 60,
    borderRadius: 16,
    iconSize: 24,
    lockOverlay: 24,
    lockIcon: 14
  }
};
var Avatar = ({ src, size = "m", type, locked }) => {
  const [imgError, setImageError] = useState(false);
  return /* @__PURE__ */ jsxs(Wrapper, { children: [
    /* @__PURE__ */ jsx(
      Container2,
      {
        size,
        type,
        alignItems: "center",
        justifyContent: "center",
        imgVisible: !!src && !imgError,
        children: src && !imgError ? /* @__PURE__ */ jsx("img", { src, alt: `${type} avatar`, onError: () => setImageError(true) }) : /* @__PURE__ */ jsx(
          Icons_default,
          {
            name: type === "person" ? "user" : "building",
            iconSize: SizeMap[size].iconSize,
            iconColor: "labelAssistive"
          }
        )
      }
    ),
    locked && /* @__PURE__ */ jsx(LockOverlay, { overlaySize: SizeMap[size].lockOverlay, children: /* @__PURE__ */ jsx(IconLockFilled, { size: SizeMap[size].lockIcon, color: "#999999" }) })
  ] });
};
var Avartar_default = Avatar;
var Wrapper = styled18.div`
  position: relative;
  flex-shrink: 0;
`;
var LockOverlay = styled18.div`
  position: absolute;
  bottom: -3px;
  right: -3px;
  width: ${({ overlaySize }) => overlaySize}px;
  height: ${({ overlaySize }) => overlaySize}px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f0f0;
  border: 2px solid white;
`;
var Container2 = styled18(Flex_default)`
  width: ${({ size }) => SizeMap[size].width}px;
  min-width: ${({ size }) => SizeMap[size].width}px;
  height: ${({ size }) => SizeMap[size].width}px;
  min-height: ${({ size }) => SizeMap[size].width}px;

  border-radius: ${({ size, type }) => type === "sponsor" ? `${SizeMap[size].borderRadius}px` : "50%"};
  overflow: hidden;
  background-color: ${({ theme }) => theme.fill.fillNeutral};
  ${({ imgVisible }) => imgVisible && css`
      border: 0.5px solid ${hexWithOpacity("commonBlack", "opacity12")};
    `}

  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
var TextColorMap = {
  neutral: "labelNeutral",
  danger: "accentRed",
  success: "accentGreen",
  warning: "accentOrange"
};
var TextSizeMap3 = {
  sm: "body4",
  md: "body3",
  lg: "body2"
};
var TextWeightMap = {
  sm: "regular",
  md: "medium",
  lg: "bold"
};
var IconSizeMap = {
  sm: 12,
  md: 16,
  lg: 20
};
var Chip = ({
  size = "md",
  type = "soft",
  children,
  variant = "normal",
  iconName,
  disabled = false,
  color = "neutral",
  onClick,
  isSelected = false
}) => {
  const textColor = useMemo(() => {
    return disabled ? "labelDisable" : color === "neutral" && isSelected ? "primaryNormal" : TextColorMap[color];
  }, [color, disabled, isSelected]);
  return /* @__PURE__ */ jsxs(
    Container3,
    {
      type,
      alignItems: "center",
      size,
      color,
      variant,
      disabled,
      onClick,
      isSelected,
      gap: 6,
      children: [
        variant === "startIcon" && iconName && /* @__PURE__ */ jsx(Icons_default, { name: iconName, iconSize: IconSizeMap[size], iconColor: textColor }),
        /* @__PURE__ */ jsx(Text_default, { color: textColor, size: TextSizeMap3[size], fontWeight: TextWeightMap[size], as: "span", children }),
        variant === "endIcon" && iconName && /* @__PURE__ */ jsx(Icons_default, { name: iconName, iconSize: IconSizeMap[size], iconColor: textColor })
      ]
    }
  );
};
var Chip_default = Chip;
var ContainerSizeMap = {
  sm: 20,
  md: 32,
  lg: 40
};
var ContainerBorderRadiusMap = {
  sm: 6,
  md: 8,
  lg: 10
};
var ContainerPaddingMap = {
  sm: "0 6px",
  md: "0 10px",
  lg: "0 14px"
};
var Container3 = styled18(Flex_default)`
  width: fit-content;
  height: ${({ size }) => ContainerSizeMap[size]}px;
  border-radius: ${({ size }) => ContainerBorderRadiusMap[size]}px;
  padding: ${({ size }) => ContainerPaddingMap[size]};
  background-color: ${({ theme }) => theme.common.commonWhite};

  &:hover {
    cursor: pointer;
    background-color: ${({ theme, color, isSelected }) => color === "neutral" ? isSelected ? theme.primary.primaryAssistive : theme.fill.fillAssistive : color === "danger" ? theme.accent.accentRedAssistive : color === "success" ? theme.accent.accentGreenAssistive : theme.accent.accentOrangeAssistive};
  }

  ${({ isSelected, color, theme, disabled }) => {
  if (!disabled && isSelected) {
    return css`
        background-color: ${color === "neutral" ? theme.primary.primaryAlternative : color === "danger" ? theme.accent.accentRedAlternative : color === "success" ? theme.accent.accentGreenAlternative : theme.accent.accentOrangeAlternative};
      `;
  }
}}

  ${({ type, color, isSelected, theme }) => type === "outline" && css`
      border: 1px solid
        ${color === "neutral" ? isSelected ? theme.primary.primaryNeutral : theme.line.lineNormal : color === "danger" ? theme.accent.accentRedNeutral : color === "success" ? theme.accent.accentGreenNeutral : theme.accent.accentOrangeNeutral};
    `}
`;
var OutsideClickHandler = ({ children, onOutsideClick, className }) => {
  const ref = useRef(null);
  const onOutsideClickRef = useRef(onOutsideClick);
  useEffect(() => {
    onOutsideClickRef.current = onOutsideClick;
  });
  useEffect(() => {
    function handleClick(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        onOutsideClickRef.current();
      }
    }
    const frameId = requestAnimationFrame(() => {
      document.addEventListener("mousedown", handleClick);
    });
    return () => {
      cancelAnimationFrame(frameId);
      document.removeEventListener("mousedown", handleClick);
    };
  }, []);
  return /* @__PURE__ */ jsx("div", { ref, className, children });
};
var OutsideClickHandler_default = OutsideClickHandler;
var Select = ({
  selectedOption,
  size = "medium",
  type = "soft",
  disabled = false,
  placeholder = "Select an option",
  width = "fit-content",
  initialOpen = false,
  optionsPosition = "left",
  options,
  CustomOptionComponent,
  onChange
}) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const isActive = useMemo(() => {
    return isOpen || !!selectedOption;
  }, [isOpen, selectedOption]);
  const textColor = useMemo(() => {
    if (disabled) return "labelDisable";
    if (isActive) return "primaryNormal";
    return "labelNeutral";
  }, [disabled, isActive]);
  const selectedLabel = useMemo(() => {
    if (!selectedOption) return placeholder;
    return options.find((i) => i.value === selectedOption)?.label ?? placeholder;
  }, [selectedOption, options, placeholder]);
  return /* @__PURE__ */ jsxs(Wrapper2, { onOutsideClick: () => setIsOpen(false), children: [
    /* @__PURE__ */ jsxs(
      Container4,
      {
        as: "button",
        size,
        kind: type,
        type: "button",
        disabled,
        alignItems: "center",
        justifyContent: "space-between",
        width,
        isActive,
        onClick: () => setIsOpen(!isOpen),
        children: [
          /* @__PURE__ */ jsx(Text_default, { size: SelectStyleMap[size].textSize, fontWeight: "medium", color: textColor, children: selectedLabel }),
          /* @__PURE__ */ jsx(Icons_default, { name: "unfold", iconSize: SelectStyleMap[size].iconSize, iconColor: textColor })
        ]
      }
    ),
    isOpen && /* @__PURE__ */ jsx(
      List,
      {
        direction: "column",
        position: optionsPosition,
        size,
        onClick: () => setIsOpen(false),
        children: options?.map(
          (option) => CustomOptionComponent ? /* @__PURE__ */ jsx("button", { onClick: () => onChange(option.value), children: /* @__PURE__ */ jsx(CustomOptionComponent, { ...option }, option.value) }, option.value) : /* @__PURE__ */ jsx(
            "button",
            {
              className: "defaultBtn",
              onClick: () => onChange(option.value),
              children: /* @__PURE__ */ jsx(Text_default, { size: "body3", fontWeight: "medium", color: "labelNeutral", children: option.label }, option.value)
            },
            option.value
          )
        )
      }
    )
  ] });
};
var Select_default = Select;
var SelectStyleMap = {
  small: {
    height: 32,
    borderRadius: 8,
    padding: "0 10px 0 14px",
    textSize: "body4",
    iconSize: 14
  },
  medium: {
    height: 40,
    borderRadius: 10,
    padding: "0 12px 0 16px",
    textSize: "body3",
    iconSize: 16
  },
  large: {
    height: 48,
    borderRadius: 12,
    padding: "0 12px 0 16px",
    textSize: "body2",
    iconSize: 20
  }
};
var Wrapper2 = styled18(OutsideClickHandler_default)`
  position: relative;
`;
var Container4 = styled18(Flex_default)`
  position: relative;
  width: ${({ width }) => typeof width === "number" ? `${width}px` : width};
  height: ${({ size }) => SelectStyleMap[size].height}px;
  border-radius: ${({ size }) => SelectStyleMap[size].borderRadius}px;
  padding: ${({ size }) => SelectStyleMap[size].padding};

  ${({ kind, theme, disabled, isActive }) => {
  if (disabled)
    return css`
        background-color: ${kind === "soft" ? theme.fill.fillAssistive : theme.common.commonWhite};
        border: ${kind === "outline" ? `1px solid ${theme.line.lineNormal}` : "none"};
        cursor: not-allowed;
      `;
  if (isActive)
    return css`
        background-color: ${kind === "soft" ? theme.primary.primaryAlternative : theme.common.commonWhite};
        border: ${kind === "outline" ? `1px solid ${theme.primary.primaryNormal}` : "none"};
      `;
  if (kind === "soft")
    return css`
        background-color: ${theme.fill.fillAssistive};

        &:hover {
          background-color: ${theme.fill.fillNeutral};
          p {
            color: ${theme.label.labelStrong};
          }
        }
      `;
  return css`
      background-color: ${theme.common.commonWhite};
      border: 1px solid ${theme.line.lineNormal};

      &:hover {
        background-color: ${theme.fill.fillAssistive};
        p {
          color: ${theme.label.labelStrong};
        }
      }
    `;
}}
`;
var List = styled18(Flex_default)`
  position: absolute;
  min-width: 100%;
  width: fit-content;
  top: ${({ size }) => SelectStyleMap[size].height + 12}px;
  background-color: ${({ theme }) => theme.common.commonWhite};
  border-radius: 10px;
  box-shadow: ${({ theme }) => theme.shadow.shadowM};
  border: 1px solid ${({ theme }) => theme.line.lineNeutral};
  z-index: 210;
  overflow: hidden;

  ${({ position }) => position === "right" ? css`
          right: 0;
        ` : css`
          left: 0;
        `}

  > * {
    white-space: nowrap;
  }

  > button {
    padding: 0 8px;
    &:hover {
      background-color: ${({ theme }) => theme.fill.fillAssistive};
    }
  }

  .defaultBtn {
    height: 40px;
    padding: 0 16px;
  }
`;

// src/utils/authApi.ts
var readData = async (request) => {
  const response = await request;
  return response.data;
};
var authApi = {
  signIn: (client, payload) => readData(client.post("/v1/signin", payload)),
  signUp: (client, payload) => readData(client.post("/v2/users/signup", payload)),
  postVerification: (client, payload) => readData(client.post("/v1/verification", payload)),
  checkVerification: (client, payload) => readData(client.post("/v1/verification/check", payload)),
  changePassword: (client, payload) => readData(client.put("/v1/me/password", payload)),
  signOut: (client) => readData(client.post("/v1/signout")),
  withdraw: (client) => readData(client.delete("/v1/me"))
};

export { Avartar_default as Avatar, Button_default as Button, Checkbox_default as Checkbox, Chip_default as Chip, CollableThemeProvider_default as CollableThemeProvider, Flex_default as Flex, Grid_default as Grid, Icons_default as Icon, Input_default as Input, LabelForm_default as LabelForm, OutsideClickHandler_default as OutsideClickHandler, Pagination_default as Pagination, Popup_default as Popup, Radio_default as Radio, SearchForm_default as SearchForm, Search_default2 as SearchInput, SegmentedControl_default as SegmentedControl, Select_default as Select, Tabs_default2 as Tabs, Tag_default2 as Tag, Text_default as Text, Title_default as Title, ToastPopup_default as ToastPopup, ToastPopupProvider, Tooltip_default as Tooltip, authApi, themes as collableTheme, useIsMounted, useRadio_default as useRadio, useToastPopup };
